import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { Zap, Clock, Trash2, X, ChevronDown, ChevronRight, RefreshCw, History } from 'lucide-react';
import CakeStakesText from '../images/CakeStakesText.png';
import { SPORTS_CONFIG, SPORT_CATEGORIES, getSportConfig, PERMANENT_SPORT_KEYS, CATEGORY_TO_CATS, CAT_TO_ICON, getFuturesConfig } from '../config/sportsConfig';
import { getPrimaryLeagueKey, getAllowedLeagueCount, getCategoryOfActiveSport, splitLeagueName, groupLeaguesByPrefix, shouldGroupLeagues } from '../utils/homeTabHelpers';
import { GAME_MARKET_LABELS, formatBetSelection, YES_NO_MARKET_KEYS } from '../utils/betFormatting';
import CashoutModal from './CashoutModal';
import { TEAM_LOGOS } from '../utils/teamLogos';
import BottomNav from './BottomNav';
import HomeTab from './HomeTab';
import LiveTab from './LiveTab';
import FuturesTab from './FuturesTab';
import FavoritesTab from './FavoritesTab';
import MyBetsTab from './MyBetsTab';
import CasinoTab from './casino/CasinoTab';
import LeaderboardTab from './LeaderboardTab';
import GameCard from './GameCard';
import { colors, fontFamily } from '../theme';
import { useDragScroll } from '../hooks';

const CC = ({ size = '1em' }) => (
  <span style={{ fontSize: size, marginLeft: 4, verticalAlign: 'baseline' }}>₵₵</span>
);

// The home page's sport display order — SPORT_CATEGORIES' own category/array
// order — flattened into a single sport_key -> position lookup, so the Live
// tab's sport list can be sorted the same way instead of using raw API order.
const _LIVE_SPORT_ORDER = Object.values(SPORT_CATEGORIES).flat();
const LIVE_SPORT_ORDER_INDEX = (sportKey) => {
  const idx = _LIVE_SPORT_ORDER.indexOf(sportKey);
  return idx === -1 ? _LIVE_SPORT_ORDER.length : idx;
};

// Unique identity for a specific bettable outcome (game_id + market + the raw
// outcome fields that actually distinguish it, e.g. player name via outcome.name
// and Over/Under via outcome.description). Used both when a bet is added to the
// slip and when checking whether a given button is already selected, so the two
// checks can never disagree with each other.
const getBetKey = (gameId, marketKey, outcome) =>
  `${gameId}::${marketKey}::${outcome.name || ''}::${outcome.description || ''}::${outcome.point ?? ''}`;

const PROP_DISPLAY_NAMES = {
  // Standard props
  'player_assists': 'Assists',
  'player_defensive_interceptions': 'Defensive Interceptions',
  'player_field_goals': 'Field Goals',
  'player_kicking_points': 'Kicking Points',
  'player_pass_attempts': 'Pass Attempts',
  'player_pass_completions': 'Pass Completions',
  'player_pass_interceptions': 'Pass Interceptions',
  'player_pass_longest_completion': 'Longest Pass Completion',
  'player_pass_rush_yds': 'Pass + Rush Yards',
  'player_pass_rush_reception_tds': 'Pass + Rush + Reception TDs',
  'player_pass_rush_reception_yds': 'Pass + Rush + Reception Yards',
  'player_pass_tds': 'Passing TDs',
  'player_pass_yds': 'Passing Yards',
  'player_pass_yds_q1': '1st Quarter Pass Yards',
  'player_pats': 'Points After Touchdown',
  'player_receptions': 'Receptions',
  'player_reception_longest': 'Longest Reception',
  'player_reception_tds': 'Reception TDs',
  'player_reception_yds': 'Reception Yards',
  'player_rush_attempts': 'Rush Attempts',
  'player_rush_longest': 'Longest Rush',
  'player_rush_reception_tds': 'Rush + Reception TDs',
  'player_rush_reception_yds': 'Rush + Reception Yards',
  'player_rush_tds': 'Rushing TDs',
  'player_rush_yds': 'Rushing Yards',
  'player_sacks': 'Sacks',
  'player_solo_tackles': 'Solo Tackles',
  'player_tackles_assists': 'Tackles + Assists',
  'player_tds_over': 'Touchdowns (+1.5)',
  'player_1st_td': '1st Touchdown Scorer',
  'player_anytime_td': 'Anytime Touchdown',
  'player_last_td': 'Last Touchdown Scorer',
  
  // Alternate props (NFL only)
  'player_assists_alternate': 'Alt Assists',
  'player_field_goals_alternate': 'Alt Field Goals',
  'player_kicking_points_alternate': 'Alt Kicking Points',
  'player_pass_attempts_alternate': 'Alt Pass Attempts',
  'player_pass_completions_alternate': 'Alt Pass Completions',
  'player_pass_interceptions_alternate': 'Alt Pass Interceptions',
  'player_pass_longest_completion_alternate': 'Alt Longest Pass',
  'player_pass_rush_yds_alternate': 'Alt Pass + Rush Yards',
  'player_pass_rush_reception_tds_alternate': 'Alt P+R+Rec TDs',
  'player_pass_rush_reception_yds_alternate': 'Alt P+R+Rec Yards',
  'player_pass_tds_alternate': 'Alt Passing TDs',
  'player_pass_yds_alternate': 'Alt Passing Yards',
  'player_pats_alternate': 'Alt PAT',
  'player_receptions_alternate': 'Alt Receptions',
  'player_reception_longest_alternate': 'Alt Longest Reception',
  'player_reception_tds_alternate': 'Alt Reception TDs',
  'player_reception_yds_alternate': 'Alt Reception Yards',
  'player_rush_attempts_alternate': 'Alt Rush Attempts',
  'player_rush_longest_alternate': 'Alt Longest Rush',
  'player_rush_reception_tds_alternate': 'Alt Rush + Rec TDs',
  'player_rush_reception_yds_alternate': 'Alt Rush + Rec Yards',
  'player_rush_tds_alternate': 'Alt Rushing TDs',
  'player_rush_yds_alternate': 'Alt Rushing Yards',
  'player_sacks_alternate': 'Alt Sacks',
  'player_solo_tackles_alternate': 'Alt Solo Tackles',
  'player_tackles_assists_alternate': 'Alt Tackles + Assists',
  
  // Basketball props
  'player_points': 'Points',
  'player_points_q1': '1st Quarter Points',
  'player_rebounds': 'Rebounds',
  'player_rebounds_q1': '1st Quarter Rebounds',
  'player_threes': '3-Pointers Made',
  'player_blocks': 'Blocks',
  'player_steals': 'Steals',
  'player_blocks_steals': 'Blocks + Steals',
  'player_turnovers': 'Turnovers',
  'player_points_rebounds_assists': 'Pts + Reb + Ast',
  'player_points_rebounds': 'Points + Rebounds',
  'player_points_assists': 'Points + Assists',
  'player_rebounds_assists': 'Rebounds + Assists',
  'player_field_goals': 'Field Goals',
  'player_frees_made': 'Free Throws Made',
  'player_frees_attempts': 'Free Throws Attempted',
  'player_first_basket': 'First Basket Scorer',
  'player_first_team_basket': 'First Team Basket',
  'player_double_double': 'Double Double',
  'player_triple_double': 'Triple Double',
  'player_method_of_first_basket': 'Method of First Basket',
  'player_shots': 'Shots',
  'player_passes': 'Passes',

  // Basketball alternate props
  'player_points_alternate': 'Alt Points',
  'player_rebounds_alternate': 'Alt Rebounds',
  'player_assists_alternate': 'Alt Assists',
  'player_blocks_alternate': 'Alt Blocks',
  'player_steals_alternate': 'Alt Steals',
  'player_turnovers_alternate': 'Alt Turnovers',
  'player_threes_alternate': 'Alt 3-Pointers',
  'player_points_assists_alternate': 'Alt Pts + Ast',
  'player_points_rebounds_alternate': 'Alt Pts + Reb',
  'player_rebounds_assists_alternate': 'Alt Reb + Ast',
  'player_points_rebounds_assists_alternate': 'Alt Pts + Reb + Ast',

  // NHL (Hockey) props
  'player_goals': 'Goals',
  'player_goals_alternate': 'Alt Goals',
  'player_shots_on_goal': 'Shots on Goal',
  'player_shots_on_goal_alternate': 'Alt Shots on Goal',
  'player_blocked_shots': 'Blocked Shots',
  'player_blocked_shots_alternate': 'Alt Blocked Shots',
  'player_power_play_points': 'Power Play Points',
  'player_power_play_points_alternate': 'Alt Power Play Points',
  'player_total_saves': 'Saves',
  'player_total_saves_alternate': 'Alt Saves',
  'player_goal_scorer_anytime': 'Anytime Goal Scorer',
  'player_goal_scorer_first': 'First Goal Scorer',
  'player_goal_scorer_last': 'Last Goal Scorer',
  // First-player props (basketball)
  'player_first_assist': 'First Assist',
  'player_first_rebound': 'First Rebound',
  // Soccer props (odds-api.io)
  'player_anytime_goalscorer': 'Anytime Goalscorer',
  'player_shots_on_target': 'Shots on Target',
  'player_shots_total': 'Total Shots',
  'player_passes': 'Passes',
  'player_tackles_soccer': 'Tackles',
  'player_cards': 'Cards',
  'player_to_be_booked': 'To Be Booked',
  'player_to_score_or_assist': 'To Score or Assist',
  'player_fouled': 'To Be Fouled',
  'player_fouls_committed': 'Fouls Committed',
  'player_goalkeeper_saves': 'Goalkeeper Saves',

  // MLB (Baseball) props - Batting
  'batter_hits': 'Hits',
  'batter_hits_alternate': 'Alt Hits',
  'batter_runs_scored': 'Runs Scored',
  'batter_runs_scored_alternate': 'Alt Runs Scored',
  'batter_rbis': 'RBIs',
  'batter_rbis_alternate': 'Alt RBIs',
  'batter_home_runs': 'Home Runs',
  'batter_home_runs_alternate': 'Alt Home Runs',
  'batter_first_home_run': 'First Home Run',
  'batter_total_bases': 'Total Bases',
  'batter_total_bases_alternate': 'Alt Total Bases',
  'batter_stolen_bases': 'Stolen Bases',
  'batter_strikeouts': 'Strikeouts (Batter)',
  'batter_strikeouts_alternate': 'Alt Strikeouts',
  'batter_walks': 'Walks (Batter)',
  'batter_walks_alternate': 'Alt Walks',
  'batter_hits_runs_rbis': 'Hits + Runs + RBIs',
  'batter_singles': 'Singles',
  'batter_singles_alternate': 'Alt Singles',
  'batter_doubles': 'Doubles',
  'batter_doubles_alternate': 'Alt Doubles',
  'batter_triples': 'Triples',
  'batter_triples_alternate': 'Alt Triples',

  // MLB (Baseball) props - Pitching
  'pitcher_strikeouts': 'Strikeouts (Pitcher)',
  'pitcher_strikeouts_alternate': 'Alt Strikeouts',
  'pitcher_outs': 'Outs Recorded',
  'pitcher_hits_allowed': 'Hits Allowed',
  'pitcher_hits_allowed_alternate': 'Alt Hits Allowed',
  'pitcher_earned_runs': 'Earned Runs',
  'pitcher_walks': 'Walks Allowed',
  'pitcher_walks_alternate': 'Alt Walks Allowed',
  'pitcher_record_a_win': 'Record a Win'
};

// Legacy - logos are now in utils/teamLogos.js
const _TEAM_LOGOS_MOVED = {
  // NFL Teams
  'Kansas City Chiefs': 'https://a.espncdn.com/i/teamlogos/nfl/500/kc.png',
  'Buffalo Bills': 'https://a.espncdn.com/i/teamlogos/nfl/500/buf.png',
  'San Francisco 49ers': 'https://a.espncdn.com/i/teamlogos/nfl/500/sf.png',
  'Dallas Cowboys': 'https://a.espncdn.com/i/teamlogos/nfl/500/dal.png',
  'Philadelphia Eagles': 'https://a.espncdn.com/i/teamlogos/nfl/500/phi.png',
  'Miami Dolphins': 'https://a.espncdn.com/i/teamlogos/nfl/500/mia.png',
  'Baltimore Ravens': 'https://a.espncdn.com/i/teamlogos/nfl/500/bal.png',
  'Cincinnati Bengals': 'https://a.espncdn.com/i/teamlogos/nfl/500/cin.png',
  'Green Bay Packers': 'https://a.espncdn.com/i/teamlogos/nfl/500/gb.png',
  'Detroit Lions': 'https://a.espncdn.com/i/teamlogos/nfl/500/det.png',
  'Los Angeles Rams': 'https://a.espncdn.com/i/teamlogos/nfl/500/lar.png',
  'Seattle Seahawks': 'https://a.espncdn.com/i/teamlogos/nfl/500/sea.png',
  'New York Jets': 'https://a.espncdn.com/i/teamlogos/nfl/500/nyj.png',
  'New York Giants': 'https://a.espncdn.com/i/teamlogos/nfl/500/nyg.png',
  'Pittsburgh Steelers': 'https://a.espncdn.com/i/teamlogos/nfl/500/pit.png',
  'Cleveland Browns': 'https://a.espncdn.com/i/teamlogos/nfl/500/cle.png',
  'Los Angeles Chargers': 'https://a.espncdn.com/i/teamlogos/nfl/500/lac.png',
  'Las Vegas Raiders': 'https://a.espncdn.com/i/teamlogos/nfl/500/lv.png',
  'Minnesota Vikings': 'https://a.espncdn.com/i/teamlogos/nfl/500/min.png',
  'Jacksonville Jaguars': 'https://a.espncdn.com/i/teamlogos/nfl/500/jax.png',
  'Tampa Bay Buccaneers': 'https://a.espncdn.com/i/teamlogos/nfl/500/tb.png',
  'Atlanta Falcons': 'https://a.espncdn.com/i/teamlogos/nfl/500/atl.png',
  'New Orleans Saints': 'https://a.espncdn.com/i/teamlogos/nfl/500/no.png',
  'Carolina Panthers': 'https://a.espncdn.com/i/teamlogos/nfl/500/car.png',
  'Indianapolis Colts': 'https://a.espncdn.com/i/teamlogos/nfl/500/ind.png',
  'Tennessee Titans': 'https://a.espncdn.com/i/teamlogos/nfl/500/ten.png',
  'Houston Texans': 'https://a.espncdn.com/i/teamlogos/nfl/500/hou.png',
  'Denver Broncos': 'https://a.espncdn.com/i/teamlogos/nfl/500/den.png',
  'Arizona Cardinals': 'https://a.espncdn.com/i/teamlogos/nfl/500/ari.png',
  'Washington Commanders': 'https://a.espncdn.com/i/teamlogos/nfl/500/wsh.png',
  'New England Patriots': 'https://a.espncdn.com/i/teamlogos/nfl/500/ne.png',
  'Chicago Bears': 'https://a.espncdn.com/i/teamlogos/nfl/500/chi.png',
  
  // NBA Teams
  'Los Angeles Lakers': 'https://a.espncdn.com/i/teamlogos/nba/500/lal.png',
  'Boston Celtics': 'https://a.espncdn.com/i/teamlogos/nba/500/bos.png',
  'Golden State Warriors': 'https://a.espncdn.com/i/teamlogos/nba/500/gs.png',
  'Phoenix Suns': 'https://a.espncdn.com/i/teamlogos/nba/500/phx.png',
  'Miami Heat': 'https://a.espncdn.com/i/teamlogos/nba/500/mia.png',
  'Milwaukee Bucks': 'https://a.espncdn.com/i/teamlogos/nba/500/mil.png',
  'Brooklyn Nets': 'https://a.espncdn.com/i/teamlogos/nba/500/bkn.png',
  'Philadelphia 76ers': 'https://a.espncdn.com/i/teamlogos/nba/500/phi.png',
  'Denver Nuggets': 'https://a.espncdn.com/i/teamlogos/nba/500/den.png',
  'Dallas Mavericks': 'https://a.espncdn.com/i/teamlogos/nba/500/dal.png',
  'Memphis Grizzlies': 'https://a.espncdn.com/i/teamlogos/nba/500/mem.png',
  'Cleveland Cavaliers': 'https://a.espncdn.com/i/teamlogos/nba/500/cle.png',
  'New York Knicks': 'https://a.espncdn.com/i/teamlogos/nba/500/ny.png',
  'Sacramento Kings': 'https://a.espncdn.com/i/teamlogos/nba/500/sac.png',
  'Los Angeles Clippers': 'https://a.espncdn.com/i/teamlogos/nba/500/lac.png',
  'Minnesota Timberwolves': 'https://a.espncdn.com/i/teamlogos/nba/500/min.png',
  'New Orleans Pelicans': 'https://a.espncdn.com/i/teamlogos/nba/500/no.png',
  'Atlanta Hawks': 'https://a.espncdn.com/i/teamlogos/nba/500/atl.png',
  'Chicago Bulls': 'https://a.espncdn.com/i/teamlogos/nba/500/chi.png',
  'Portland Trail Blazers': 'https://a.espncdn.com/i/teamlogos/nba/500/por.png',
  'Utah Jazz': 'https://a.espncdn.com/i/teamlogos/nba/500/utah.png',
  'Toronto Raptors': 'https://a.espncdn.com/i/teamlogos/nba/500/tor.png',
  'Indiana Pacers': 'https://a.espncdn.com/i/teamlogos/nba/500/ind.png',
  'Washington Wizards': 'https://a.espncdn.com/i/teamlogos/nba/500/wsh.png',
  'Charlotte Hornets': 'https://a.espncdn.com/i/teamlogos/nba/500/cha.png',
  'San Antonio Spurs': 'https://a.espncdn.com/i/teamlogos/nba/500/sa.png',
  'Oklahoma City Thunder': 'https://a.espncdn.com/i/teamlogos/nba/500/okc.png',
  'Houston Rockets': 'https://a.espncdn.com/i/teamlogos/nba/500/hou.png',
  'Orlando Magic': 'https://a.espncdn.com/i/teamlogos/nba/500/orl.png',
  'Detroit Pistons': 'https://a.espncdn.com/i/teamlogos/nba/500/det.png',
  
  // NHL Teams
  'Boston Bruins': 'https://a.espncdn.com/i/teamlogos/nhl/500/bos.png',
  'New York Rangers': 'https://a.espncdn.com/i/teamlogos/nhl/500/nyr.png',
  'Toronto Maple Leafs': 'https://a.espncdn.com/i/teamlogos/nhl/500/tor.png',
  'Montreal Canadiens': 'https://a.espncdn.com/i/teamlogos/nhl/500/mtl.png',
  'Tampa Bay Lightning': 'https://a.espncdn.com/i/teamlogos/nhl/500/tb.png',
  'Colorado Avalanche': 'https://a.espncdn.com/i/teamlogos/nhl/500/col.png',
  'Edmonton Oilers': 'https://a.espncdn.com/i/teamlogos/nhl/500/edm.png',
  'Vegas Golden Knights': 'https://a.espncdn.com/i/teamlogos/nhl/500/vgk.png',
  'Carolina Hurricanes': 'https://a.espncdn.com/i/teamlogos/nhl/500/car.png',
  'New Jersey Devils': 'https://a.espncdn.com/i/teamlogos/nhl/500/nj.png',
  'Dallas Stars': 'https://a.espncdn.com/i/teamlogos/nhl/500/dal.png',
  'Seattle Kraken': 'https://a.espncdn.com/i/teamlogos/nhl/500/sea.png',
  'Los Angeles Kings': 'https://a.espncdn.com/i/teamlogos/nhl/500/la.png',
  'Winnipeg Jets': 'https://a.espncdn.com/i/teamlogos/nhl/500/wpg.png',
  'Florida Panthers': 'https://a.espncdn.com/i/teamlogos/nhl/500/fla.png',
  'Minnesota Wild': 'https://a.espncdn.com/i/teamlogos/nhl/500/min.png',
  'Calgary Flames': 'https://a.espncdn.com/i/teamlogos/nhl/500/cgy.png',
  'Pittsburgh Penguins': 'https://a.espncdn.com/i/teamlogos/nhl/500/pit.png',
  'Nashville Predators': 'https://a.espncdn.com/i/teamlogos/nhl/500/nsh.png',
  'Vancouver Canucks': 'https://a.espncdn.com/i/teamlogos/nhl/500/van.png',
  'St. Louis Blues': 'https://a.espncdn.com/i/teamlogos/nhl/500/stl.png',
  'New York Islanders': 'https://a.espncdn.com/i/teamlogos/nhl/500/nyi.png',
  'Washington Capitals': 'https://a.espncdn.com/i/teamlogos/nhl/500/wsh.png',
  'Buffalo Sabres': 'https://a.espncdn.com/i/teamlogos/nhl/500/buf.png',
  'Detroit Red Wings': 'https://a.espncdn.com/i/teamlogos/nhl/500/det.png',
  'Ottawa Senators': 'https://a.espncdn.com/i/teamlogos/nhl/500/ott.png',
  'Philadelphia Flyers': 'https://a.espncdn.com/i/teamlogos/nhl/500/phi.png',
  'Arizona Coyotes': 'https://a.espncdn.com/i/teamlogos/nhl/500/ari.png',
  'San Jose Sharks': 'https://a.espncdn.com/i/teamlogos/nhl/500/sj.png',
  'Anaheim Ducks': 'https://a.espncdn.com/i/teamlogos/nhl/500/ana.png',
  'Chicago Blackhawks': 'https://a.espncdn.com/i/teamlogos/nhl/500/chi.png',
  'Columbus Blue Jackets': 'https://a.espncdn.com/i/teamlogos/nhl/500/cbj.png',
  
  // MLB Teams
  'New York Yankees': 'https://a.espncdn.com/i/teamlogos/mlb/500/nyy.png',
  'Boston Red Sox': 'https://a.espncdn.com/i/teamlogos/mlb/500/bos.png',
  'Los Angeles Dodgers': 'https://a.espncdn.com/i/teamlogos/mlb/500/lad.png',
  'San Francisco Giants': 'https://a.espncdn.com/i/teamlogos/mlb/500/sf.png',
  'Houston Astros': 'https://a.espncdn.com/i/teamlogos/mlb/500/hou.png',
  'Atlanta Braves': 'https://a.espncdn.com/i/teamlogos/mlb/500/atl.png',
  'New York Mets': 'https://a.espncdn.com/i/teamlogos/mlb/500/nym.png',
  'Philadelphia Phillies': 'https://a.espncdn.com/i/teamlogos/mlb/500/phi.png',
  'San Diego Padres': 'https://a.espncdn.com/i/teamlogos/mlb/500/sd.png',
  'Toronto Blue Jays': 'https://a.espncdn.com/i/teamlogos/mlb/500/tor.png',
  'St. Louis Cardinals': 'https://a.espncdn.com/i/teamlogos/mlb/500/stl.png',
  'Milwaukee Brewers': 'https://a.espncdn.com/i/teamlogos/mlb/500/mil.png',
  'Tampa Bay Rays': 'https://a.espncdn.com/i/teamlogos/mlb/500/tb.png',
  'Seattle Mariners': 'https://a.espncdn.com/i/teamlogos/mlb/500/sea.png',
  'Cleveland Guardians': 'https://a.espncdn.com/i/teamlogos/mlb/500/cle.png',
  'Minnesota Twins': 'https://a.espncdn.com/i/teamlogos/mlb/500/min.png',
  'Chicago White Sox': 'https://a.espncdn.com/i/teamlogos/mlb/500/chw.png',
  'Chicago Cubs': 'https://a.espncdn.com/i/teamlogos/mlb/500/chc.png',
  'Los Angeles Angels': 'https://a.espncdn.com/i/teamlogos/mlb/500/laa.png',
  'Texas Rangers': 'https://a.espncdn.com/i/teamlogos/mlb/500/tex.png',
  'Arizona Diamondbacks': 'https://a.espncdn.com/i/teamlogos/mlb/500/ari.png',
  'Baltimore Orioles': 'https://a.espncdn.com/i/teamlogos/mlb/500/bal.png',
  'Cincinnati Reds': 'https://a.espncdn.com/i/teamlogos/mlb/500/cin.png',
  'Colorado Rockies': 'https://a.espncdn.com/i/teamlogos/mlb/500/col.png',
  'Detroit Tigers': 'https://a.espncdn.com/i/teamlogos/mlb/500/det.png',
  'Kansas City Royals': 'https://a.espncdn.com/i/teamlogos/mlb/500/kc.png',
  'Miami Marlins': 'https://a.espncdn.com/i/teamlogos/mlb/500/mia.png',
  'Oakland Athletics': 'https://a.espncdn.com/i/teamlogos/mlb/500/oak.png',
  'Pittsburgh Pirates': 'https://a.espncdn.com/i/teamlogos/mlb/500/pit.png',
  'Washington Nationals': 'https://a.espncdn.com/i/teamlogos/mlb/500/wsh.png',

  // AFL Teams (Australian Football League)
  'Adelaide Crows': 'https://a.espncdn.com/i/teamlogos/afl/500/adel.png',
  'Brisbane Lions': 'https://a.espncdn.com/i/teamlogos/afl/500/bris.png',
  'Carlton Blues': 'https://a.espncdn.com/i/teamlogos/afl/500/carl.png',
  'Collingwood Magpies': 'https://a.espncdn.com/i/teamlogos/afl/500/coll.png',
  'Essendon Bombers': 'https://a.espncdn.com/i/teamlogos/afl/500/ess.png',
  'Fremantle Dockers': 'https://a.espncdn.com/i/teamlogos/afl/500/fre.png',
  'Geelong Cats': 'https://a.espncdn.com/i/teamlogos/afl/500/geel.png',
  'Gold Coast Suns': 'https://a.espncdn.com/i/teamlogos/afl/500/gcfc.png',
  'Greater Western Sydney Giants': 'https://a.espncdn.com/i/teamlogos/afl/500/gws.png',
  'GWS Giants': 'https://a.espncdn.com/i/teamlogos/afl/500/gws.png',
  'Hawthorn Hawks': 'https://a.espncdn.com/i/teamlogos/afl/500/haw.png',
  'Melbourne Demons': 'https://a.espncdn.com/i/teamlogos/afl/500/melb.png',
  'North Melbourne Kangaroos': 'https://a.espncdn.com/i/teamlogos/afl/500/nmfc.png',
  'Port Adelaide Power': 'https://a.espncdn.com/i/teamlogos/afl/500/port.png',
  'Richmond Tigers': 'https://a.espncdn.com/i/teamlogos/afl/500/rich.png',
  'St Kilda Saints': 'https://a.espncdn.com/i/teamlogos/afl/500/stk.png',
  'Sydney Swans': 'https://a.espncdn.com/i/teamlogos/afl/500/syd.png',
  'West Coast Eagles': 'https://a.espncdn.com/i/teamlogos/afl/500/wce.png',
  'Western Bulldogs': 'https://a.espncdn.com/i/teamlogos/afl/500/wb.png',

  // NRL Teams (National Rugby League)
  'Brisbane Broncos': 'https://a.espncdn.com/i/teamlogos/rugby/500/bris.png',
  'Canberra Raiders': 'https://a.espncdn.com/i/teamlogos/rugby/500/can.png',
  'Canterbury Bulldogs': 'https://a.espncdn.com/i/teamlogos/rugby/500/cbull.png',
  'Cronulla Sharks': 'https://a.espncdn.com/i/teamlogos/rugby/500/cro.png',
  'Gold Coast Titans': 'https://a.espncdn.com/i/teamlogos/rugby/500/gc.png',
  'Manly Sea Eagles': 'https://a.espncdn.com/i/teamlogos/rugby/500/man.png',
  'Melbourne Storm': 'https://a.espncdn.com/i/teamlogos/rugby/500/mel.png',
  'Newcastle Knights': 'https://a.espncdn.com/i/teamlogos/rugby/500/new.png',
  'North Queensland Cowboys': 'https://a.espncdn.com/i/teamlogos/rugby/500/nqc.png',
  'Parramatta Eels': 'https://a.espncdn.com/i/teamlogos/rugby/500/par.png',
  'Penrith Panthers': 'https://a.espncdn.com/i/teamlogos/rugby/500/pen.png',
  'South Sydney Rabbitohs': 'https://a.espncdn.com/i/teamlogos/rugby/500/sou.png',
  'St George Illawarra Dragons': 'https://a.espncdn.com/i/teamlogos/rugby/500/stg.png',
  'Sydney Roosters': 'https://a.espncdn.com/i/teamlogos/rugby/500/syd.png',
  'Wests Tigers': 'https://a.espncdn.com/i/teamlogos/rugby/500/wst.png',
  'New Zealand Warriors': 'https://a.espncdn.com/i/teamlogos/rugby/500/nzw.png',
  'Dolphins': 'https://a.espncdn.com/i/teamlogos/rugby/500/dol.png',

  // Super Rugby Teams
  'Blues': 'https://a.espncdn.com/i/teamlogos/rugby/500/blu.png',
  'Chiefs': 'https://a.espncdn.com/i/teamlogos/rugby/500/chi.png',
  'Crusaders': 'https://a.espncdn.com/i/teamlogos/rugby/500/cru.png',
  'Highlanders': 'https://a.espncdn.com/i/teamlogos/rugby/500/high.png',
  'Hurricanes': 'https://a.espncdn.com/i/teamlogos/rugby/500/hur.png',
  'Brumbies': 'https://a.espncdn.com/i/teamlogos/rugby/500/brum.png',
  'Reds': 'https://a.espncdn.com/i/teamlogos/rugby/500/reds.png',
  'Waratahs': 'https://a.espncdn.com/i/teamlogos/rugby/500/war.png',
  'Western Force': 'https://a.espncdn.com/i/teamlogos/rugby/500/wfor.png',
  'Fijian Drua': 'https://a.espncdn.com/i/teamlogos/rugby/500/fij.png',
  'Moana Pasifika': 'https://a.espncdn.com/i/teamlogos/rugby/500/moa.png',

  // Cricket Teams (International)
  'Australia': 'https://a.espncdn.com/i/teamlogos/cricket/500/aus.png',
  'England': 'https://a.espncdn.com/i/teamlogos/cricket/500/eng.png',
  'India': 'https://a.espncdn.com/i/teamlogos/cricket/500/ind.png',
  'New Zealand': 'https://a.espncdn.com/i/teamlogos/cricket/500/nz.png',
  'Pakistan': 'https://a.espncdn.com/i/teamlogos/cricket/500/pak.png',
  'South Africa': 'https://a.espncdn.com/i/teamlogos/cricket/500/sa.png',
  'West Indies': 'https://a.espncdn.com/i/teamlogos/cricket/500/wi.png',
  'Sri Lanka': 'https://a.espncdn.com/i/teamlogos/cricket/500/sl.png',
  'Bangladesh': 'https://a.espncdn.com/i/teamlogos/cricket/500/ban.png',
  'Afghanistan': 'https://a.espncdn.com/i/teamlogos/cricket/500/afg.png',
};

/** Hook: makes a scrollable container draggable and hides the scrollbar. */
const TAB_TITLES = {
  home: 'Home',
  live: 'Live',
  futures: 'Futures',
  casino: 'Casino',
  favorites: 'Favorites',
  mybets: 'My Bets',
  support: 'Support',
};

export default function CustomerView({ user, onLogout }) {
  const sportsbookOn = user?.sportsbook_access !== false;
  const [activeTab, setActiveTab] = useState(sportsbookOn ? 'home' : (user?.casino_access ? 'casino' : 'mybets'));

  // If the book has turned off this customer's sportsbook access, keep them
  // out of the sportsbook tabs entirely (Casino / My Bets / Support only).
  useEffect(() => {
    if (!sportsbookOn && !['casino', 'mybets', 'support'].includes(activeTab)) {
      setActiveTab(user?.casino_access ? 'casino' : 'mybets');
    }
  }, [sportsbookOn, activeTab, user?.casino_access]);

  useEffect(() => {
    document.title = `${TAB_TITLES[activeTab] || 'Home'} | CakeStakes`;
  }, [activeTab]);

  // Small self-dismissing notification — replaces browser alert() popups
  // everywhere in the customer app.
  const [toast, setToast] = useState(null); // { message, type: 'success' | 'error' }
  const toastTimerRef = useRef(null);
  const showToast = useCallback((message, type = 'success') => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 2200);
  }, []);
  const [activeSport, setActiveSport] = useState(null);
  const [games, setGames] = useState({});
  const [loadedSports, setLoadedSports] = useState(new Set());
  const [myBets, setMyBets] = useState([]);
  const [myParlays, setMyParlays] = useState([]);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [weeklyBalance, setWeeklyBalance] = useState(0);
  const [freePlayBalance, setFreePlayBalance] = useState(0);
  const [useFreePlay, setUseFreePlay] = useState(false);
  const [favoriteTeams, setFavoriteTeams] = useState([]);
  const [favoriteGames, setFavoriteGames] = useState([]);

  // Futures (The Odds API v4)
  const [futuresData, setFuturesData] = useState(null);
  const [futuresLoading, setFuturesLoading] = useState(false);
  const [activeFuturesLeague, setActiveFuturesLeague] = useState(null);

  // Dynamic leagues from GoalServe active-leagues endpoint
  // { [cat]: { loading: bool, leagues: [{id, name, matches, sport_key}] } }
  const [activeLeaguesData, setActiveLeaguesData] = useState({});

  const [activeLeagueFilter, setActiveLeagueFilter] = useState(null);
  // Which league GROUP (e.g. "League of Legends") is active for sports whose
  // leagues get grouped — see groupLeaguesByPrefix in homeTabHelpers.
  const [activeLeagueGroup, setActiveLeagueGroup] = useState(null);

  // Bet slip state
  const [betSlip, setBetSlip] = useState([]);
  const [showBetSlip, setShowBetSlip] = useState(false);
  const [betAmount, setBetAmount] = useState('');
  const [isParlayMode, setIsParlayMode] = useState(false);
  
  // Player props state
  const [selectedGameForProps, setSelectedGameForProps] = useState(null);
  const [playerProps, setPlayerProps] = useState(null);
  const [loadingProps, setLoadingProps] = useState(false);

  // Bet history modal state
  const [betHistoryTab, setBetHistoryTab] = useState('pending'); // 'pending', 'past'
  const [betHistoryPeriod, setBetHistoryPeriod] = useState('week'); // 'week', 'month', 'all'
  const [pastBets, setPastBets] = useState([]);
  const [pastParlays, setPastParlays] = useState([]);
  const [loadingBetHistory, setLoadingBetHistory] = useState(false);
  const [gameProps, setGameProps] = useState(null);
  const [loadingGameProps, setLoadingGameProps] = useState(false);

  // Messages
  const [unreadCount, setUnreadCount] = useState(0);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showMultiBetModal, setShowMultiBetModal] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [messageView, setMessageView] = useState('inbox');

  // Search
  const [teamSearchQuery, setTeamSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchedGame, setSearchedGame] = useState(null); // Track specific game from search
  const [searchedGames, setSearchedGames] = useState(null); // Array from an "Enter"-triggered multi-game search

  // Cashout state
  const [cashoutParlay, setCashoutParlay] = useState(null); // Parlay being cashed out
  const [cashoutBet, setCashoutBet] = useState(null); // Single bet being cashed out
  const [cashoutLiveBet, setCashoutLiveBet] = useState(null); // Live single bet being cashed out
  const [cashoutLiveParlay, setCashoutLiveParlay] = useState(null); // Live parlay being cashed out
  const [pendingLiveBets, setPendingLiveBets] = useState([]);
  const [pendingLiveParlays, setPendingLiveParlays] = useState([]);

  // ── Live betting state (completely separate from prematch) ──────────────
  const [activeLiveSport, setActiveLiveSport] = useState(null);
  const [liveSports, setLiveSports] = useState([]);
  const [liveOdds, setLiveOdds] = useState([]);
  const [liveBetSlip, setLiveBetSlip] = useState([]);
  const [showLiveBetSlip, setShowLiveBetSlip] = useState(false);
  const liveEsRef = useRef(null);

  const fetchLiveSports = async () => {
    try {
      console.log('[Live] fetchLiveSports called');
      const data = await api.getLiveSports();
      console.log('[Live] /api/live/sports response:', data);
      // Order live sports the same way the home page does (SPORT_CATEGORIES'
      // own key/array order) instead of whatever order the backend happens to
      // return — otherwise the tab order just reflects API response order,
      // which is unrelated to the home page's curated sport ordering.
      const sports = [...(data.sports || [])].sort(
        (a, b) => LIVE_SPORT_ORDER_INDEX(a.sport_key) - LIVE_SPORT_ORDER_INDEX(b.sport_key)
      );
      setLiveSports(sports);
      setActiveLiveSport(prev => prev || (sports.length > 0 ? sports[0].sport_key : null));
    } catch (err) {
      console.error('[Live] Failed to fetch live sports:', err);
    }
  };

  const addToLiveBetSlip = (game, market, outcome) => {
    if (user.is_read_only) {
      showToast('Your account is view-only — betting is disabled', 'error');
      return;
    }
    setLiveBetSlip(prev => {
      const existingIdx = prev.findIndex(
        b => b.game.id === game.id && b.market.key === market.key && b.outcome.name === outcome.name
      );
      if (existingIdx >= 0) {
        return prev.filter((_, i) => i !== existingIdx);
      }
      return [...prev, { game, market, outcome, price: outcome.price, american: outcome.american }];
    });
    setShowLiveBetSlip(true);
  };

  // Poll live sports every 30s while on the live tab
  useEffect(() => {
    if (activeTab !== 'live') return;
    fetchLiveSports();
    const interval = setInterval(fetchLiveSports, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // SSE stream: real-time WS odds updates (with auto-reconnect on error)
  useEffect(() => {
    if (activeTab !== 'live' || !activeLiveSport) {
      if (liveEsRef.current) {
        liveEsRef.current.close();
        liveEsRef.current = null;
      }
      return;
    }

    // Clear stale odds when switching sports
    setLiveOdds([]);

    let reconnectTimer = null;

    const connect = () => {
      if (liveEsRef.current) {
        liveEsRef.current.close();
        liveEsRef.current = null;
      }
      const url = api.getLiveStreamUrl(activeLiveSport);
      console.log('[Live] SSE connecting to', url);
      const es = new EventSource(url, { withCredentials: true });
      liveEsRef.current = es;

      es.addEventListener('odds', (e) => {
        try {
          const data = JSON.parse(e.data);
          console.log('[Live] SSE odds event — games:', data.games?.length, 'timestamp:', data.timestamp);
          if (data.games?.length > 0) {
            setLiveOdds(data.games);
          }
        } catch {}
      });

      es.onerror = (err) => {
        console.error('[Live] SSE error, reconnecting in 5s', err);
        es.close();
        liveEsRef.current = null;
        reconnectTimer = setTimeout(connect, 5000);
      };
    };

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (liveEsRef.current) {
        liveEsRef.current.close();
        liveEsRef.current = null;
      }
    };
  }, [activeTab, activeLiveSport]);

  useEffect(() => {
    if (!activeTab) return;
    setActiveLeagueFilter(null);
    setActiveLeagueGroup(null);
    if (activeTab === 'favorites') {
      loadFavoriteGames();
    } else if (activeTab === 'home' && activeSport && activeSport !== 'all') {
      if (!loadedSports.has(activeSport)) {
        loadGamesBySport(activeSport);
      }
    } else if (activeTab === 'futures') {
      if (!futuresData) {
        loadFutures();
      }
    }
    // activeLiveSport is included so switching esports (or MMA/tennis, etc.)
    // sports on the Live tab also resets the league-group filter — otherwise
    // a stale filter from the previous live sport (e.g. "Dota 2") could
    // match nothing in the new one and show an empty list.
  }, [activeTab, activeSport, activeLiveSport]);

  // Book's enabled sports list: null = all allowed, array = specific sports
  const [enabledSports, setEnabledSports] = useState(null);

  // Separate initial load
  useEffect(() => {
    loadData();
    checkUnread();
    // Load which sports this book allows
    api.getMyBookSports().then(data => {
      const sports = data.enabled_sports ?? null;
      setEnabledSports(sports);
      // Open on the Football category by default instead of landing on "All Sports"
      toggleCategory('American Football', sports);
    }).catch(() => {}); // silently ignore — default to all
  }, []);

  // Reload when switching back to betting tabs
  useEffect(() => {
    if (activeTab !== 'support') {
      // Reload user data to get fresh balances
      loadData();
      // Also refresh pending live bets/parlays so they're present the moment
      // "My Bets" is first opened, not just after a later loadBetHistory() call
      // (period change / game-modal close / cashout) — those still call it too.
      loadLiveBetHistory();
    }
  }, [activeTab]);

  // Silent background refresh of odds every 5 minutes while on a sport tab
  useEffect(() => {
    if (activeTab !== 'home' || !activeSport || activeSport === 'all') return;

    const silentRefresh = async () => {
      try {
        const data = await api.getGames(activeSport);
        setGames(prev => ({ ...prev, [activeSport]: data.games || [] }));
      } catch (err) {
        // Silent — don't interrupt the user
      }
    };

    const interval = setInterval(silentRefresh, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(interval);
  }, [activeTab, activeSport]);

  const checkUnread = async () => {
    try {
      const data = await api.getUnreadCount();
      setUnreadCount(data.unread_count);
    } catch (err) {
      console.error('Failed to get unread count:', err);
    }
  };

  const loadMessages = async () => {
    try {
      const data = messageView === 'inbox' 
        ? await api.getInbox() 
        : await api.getSentMessages();
      setMessages(data.messages || []);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const openMessage = async (message) => {
    try {
      const data = await api.getMessage(message.id);
      setSelectedMessage(data);
      if (!message.is_read && messageView === 'inbox') {
        await api.markMessageRead(message.id);
        await checkUnread();
      }
    } catch (err) {
      console.error('Failed to open message:', err);
    }
  };

  const sendNewMessage = async (subject, messageText) => {
    try {
      await api.sendMessage(subject, messageText, null);
      showToast('Message sent to customer support!');
      setShowMessageModal(false);
      await loadMessages();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send message', 'error');
    }
  };

  const replyToMessage = async (messageId, replyText) => {
    try {
      await api.replyToMessage(messageId, replyText);
      showToast('Reply sent!');
      await openMessage({ id: messageId });
      await loadMessages();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to send reply', 'error');
    }
  };


  const loadData = async () => {
    try {
      const [betsData, accountData, favoritesData] = await Promise.all([
        api.getBetHistory(user.id, 'pending'),
        api.getAccount(user.id),
        api.getFavoriteTeams()
      ]);

      const singleBets = betsData.single_bets || [];
      const parlays = betsData.parlays || [];
      
      setMyBets(singleBets);
      setMyParlays(parlays);
      
      const singlePending = singleBets.reduce((sum, bet) => sum + bet.amount, 0);
      const parlayPending = parlays.reduce((sum, parlay) => sum + parlay.amount, 0);
      setPendingTotal(singlePending + parlayPending);
      
      const account = accountData.user;
      const weeklyBal = account.weekly_balance || 0;

      // Use credit system: available_credit from backend
      setAvailableBalance(account.available_credit || 0);
      setWeeklyBalance(weeklyBal);
      setFreePlayBalance(account.free_play_balance || 0);

      setFavoriteTeams(favoritesData.favorites || []);

      // Games are NOT loaded on initial mount - only when the user switches tabs
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadBetHistory = async (period = 'week') => {
    setLoadingBetHistory(true);
    try {
      const betsData = await api.getBetHistory(user.id, 'all', period);
      const singleBets = betsData.single_bets || [];
      const parlays = betsData.parlays || [];

      // Separate past bets (settled) from pending
      setPastBets(singleBets.filter(b => b.status !== 'pending'));
      setPastParlays(parlays.filter(p => p.status !== 'pending'));
    } catch (err) {
      console.error('Error loading bet history:', err);
    } finally {
      setLoadingBetHistory(false);
    }
  };

  const loadLiveBetHistory = async () => {
    try {
      const [betsData, parlaysData] = await Promise.all([api.getLiveBets(), api.getLiveParlays()]);
      setPendingLiveBets(betsData.pending || []);
      setPendingLiveParlays(parlaysData.pending || []);
    } catch (err) {
      console.error('[LiveBets] Failed to load:', err);
    }
  };

  const loadGamesBySport = async (sportKey = null) => {
    try {
      setLoading(true);
      if (sportKey && sportKey !== 'all') {
        // Load only the specific sport
        const data = await api.getGames(sportKey);
        setGames(prev => ({ ...prev, [sportKey]: data.games || [] }));
        setLoadedSports(prev => new Set([...prev, sportKey]));
      } else {
        // Load all games (for initial load or 'all' view)
        const data = await api.getAllGames();
        const sportGames = data.games_by_sport || {};
        setGames(sportGames);
        setLoadedSports(new Set(Object.keys(sportGames)));
      }
    } catch (err) {
      console.error('Failed to fetch games:', err);
    } finally {
      setLoading(false);
    }
  };

  // Tracks the id of the game the props modal is currently showing. Both prop fetches
  // check this ref before applying their result, so a response for a game the user has
  // since navigated away from (or closed the modal on) can never overwrite state for
  // whatever game is currently selected — prevents cross-contaminating game A's markets
  // into game B's modal when responses resolve out of order.
  const propsRequestIdRef = useRef(null);

  const loadPlayerProps = async (game) => {
    setSelectedGameForProps(game);
    setPlayerProps(null);
    setLoadingProps(true);
    try {
      // Use dedicated player-props endpoint — separate cache key from game props,
      // so clicking player props doesn't trigger a large combined fetch.
      const data = await api.getEventPlayerProps(game.sport_key, game.id);
      if (propsRequestIdRef.current === game.id) {
        setPlayerProps({ bookmakers: data.bookmakers || [] });
      }
    } catch (err) {
      console.error('Failed to load player props:', err);
      if (propsRequestIdRef.current === game.id) {
        setPlayerProps(null);
      }
    } finally {
      if (propsRequestIdRef.current === game.id) {
        setLoadingProps(false);
      }
    }
  };

  const loadGameProps = async (game) => {
    setLoadingGameProps(true);
    try {
      const data = await api.getEventGameMarkets(game.sport_key, game.id);
      if (propsRequestIdRef.current === game.id) {
        setGameProps({ bookmakers: data.bookmakers || [] });
      }
    } catch (err) {
      console.error('Failed to load game props:', err);
      if (propsRequestIdRef.current === game.id) {
        setGameProps(null);
      }
    } finally {
      if (propsRequestIdRef.current === game.id) {
        setLoadingGameProps(false);
      }
    }
  };

  // Fires both prop fetches together — feeds the single merged "Props" modal below.
  const loadAllProps = (game) => {
    propsRequestIdRef.current = game.id;
    setShowBetSlip(false); // don't let the two overlap and clutter the screen
    loadPlayerProps(game);
    loadGameProps(game);
  };

  const closeProps = () => {
    propsRequestIdRef.current = null;
    setSelectedGameForProps(null);
    setPlayerProps(null);
    setGameProps(null);
  };

  const loadFavoriteGames = useCallback(() => {
    // Filter from already-loaded games in state — fast, no extra network call
    const favGames = [];
    const seen = new Set();
    Object.values(games).forEach(sportGames => {
      if (!Array.isArray(sportGames)) return;
      sportGames.forEach(game => {
        if (!seen.has(game.id) &&
            (favoriteTeams.includes(game.home_team) || favoriteTeams.includes(game.away_team))) {
          seen.add(game.id);
          favGames.push(game);
        }
      });
    });
    setFavoriteGames(favGames);
  }, [games, favoriteTeams]);

  const loadFutures = async () => {
    if (futuresLoading) return;
    setFuturesLoading(true);
    try {
      const data = await api.getFutures();
      const futures = data.futures || {};
      setFuturesData(futures);
      // Auto-select first available league
      const firstMarket = Object.values(futures).flat()[0];
      if (firstMarket) setActiveFuturesLeague(firstMarket.key);
    } catch (err) {
      console.error('Failed to load futures:', err);
      setFuturesData({});
    } finally {
      setFuturesLoading(false);
    }
  };

  const searchTeams = (query) => {
    setTeamSearchQuery(query);

    if (query.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const results = [];

    // Build category → sportKey reverse lookup
    const sportKeyToCategory = {};
    Object.entries(SPORT_CATEGORIES).forEach(([cat, keys]) => {
      keys.forEach(k => { sportKeyToCategory[k] = cat; });
    });

    // Search permanent leagues by name
    Object.entries(SPORT_CATEGORIES).forEach(([category, sportKeys]) => {
      sportKeys.forEach(key => {
        if (!PERMANENT_SPORT_KEYS.has(key)) return;
        const cfg = getSportConfig(key);
        if (cfg.name.toLowerCase().includes(lowerQuery)) {
          results.push({ type: 'league', sportKey: key, category, leagueName: cfg.name, icon: cfg.icon });
        }
      });
    });

    // Search active dynamic leagues by name
    Object.entries(activeLeaguesData).forEach(([cat, data]) => {
      if (!data?.leagues) return;
      const category = Object.entries(CATEGORY_TO_CATS).find(([, cats]) => cats.includes(cat))?.[0];
      if (!category) return;
      data.leagues.forEach(league => {
        const cleanName = league.name.includes(':') ? league.name.split(':').slice(1).join(':').trim() : league.name;
        if (cleanName.toLowerCase().includes(lowerQuery)) {
          results.push({ type: 'dynamic_league', cat, leagueId: league.id, leagueName: cleanName, category, icon: CAT_TO_ICON[cat] || '🎯' });
        }
      });
    });

    // Search through all loaded games by team name
    Object.entries(games).forEach(([sportKey, sportGames]) => {
      if (!sportGames || !Array.isArray(sportGames)) return;
      sportGames.forEach(game => {
        if (!game.home_team || !game.away_team) return;
        const homeTeam = game.home_team.toLowerCase();
        const awayTeam = game.away_team.toLowerCase();
        if (homeTeam.includes(lowerQuery) || awayTeam.includes(lowerQuery)) {
          results.push({
            type: 'game',
            game,
            sportKey,
            matchedTeam: homeTeam.includes(lowerQuery) ? game.home_team : game.away_team
          });
        }
      });
    });

    setSearchResults(results);
  };

  const clearSearch = () => {
    setTeamSearchQuery('');
    setSearchResults([]);
  };

  const selectSearchResult = (result) => {
    if (result.type === 'league') {
      loadActiveLeagues(result.category);
      setActiveTab('home');
      setActiveSport(result.sportKey);
    } else if (result.type === 'dynamic_league') {
      handleDynamicLeagueClick(result.cat, result.leagueId, result.leagueName);
    } else {
      setSearchedGames(null);
      setSearchedGame(result.game);
      setActiveTab('home');
      setActiveSport(result.sportKey);
    }
    clearSearch();
  };

  // Enter in the search box: show EVERY matching game, not just the first hit.
  const showAllSearchResults = () => {
    const gameResults = searchResults.filter(r => r.type === 'game').map(r => r.game);
    if (gameResults.length > 0) {
      setSearchedGame(null);
      setSearchedGames(gameResults);
      setActiveTab('home');
      setActiveSport('all');
      clearSearch();
      return;
    }
    // No team matches — fall back to the first league/dynamic-league hit.
    if (searchResults.length > 0) selectSearchResult(searchResults[0]);
  };

  const toggleCategory = (category, sportsOverride = enabledSports) => {
    const currentCategory = getCategoryOfActiveSport(activeSport);
    if (currentCategory === category) {
      // Tapping the already-active category again deselects it
      setActiveSport(null);
      return;
    }
    setActiveTab('home');
    const primaryKey = getPrimaryLeagueKey(category, sportsOverride);
    setActiveSport(primaryKey);
    if (getAllowedLeagueCount(category, sportsOverride) > 1) {
      loadActiveLeagues(category);
    }
    // useEffect on activeSport handles loading games
  };

  const loadActiveLeagues = async (category) => {
    const cats = CATEGORY_TO_CATS[category] || [];
    for (const cat of cats) {
      // Skip if already loaded (or loading)
      if (activeLeaguesData[cat]?.leagues || activeLeaguesData[cat]?.loading) continue;
      setActiveLeaguesData(prev => ({ ...prev, [cat]: { loading: true, leagues: [] } }));
      try {
        const data = await api.getActiveLeagues(cat);
        setActiveLeaguesData(prev => ({
          ...prev,
          [cat]: { loading: false, leagues: data.leagues || [] }
        }));
      } catch (err) {
        setActiveLeaguesData(prev => ({ ...prev, [cat]: { loading: false, leagues: [] } }));
      }
    }
  };

  const handleDynamicLeagueClick = (cat, leagueId, leagueName) => {
    const sportKey = `dynamic::${cat}::${leagueId}`;
    setActiveTab('home');
    setActiveSport(sportKey);
    if (!loadedSports.has(sportKey)) {
      loadGamesBySport(sportKey);
    }
  };

  const toggleFavoriteTeam = async (teamName) => {
    try {
      if (favoriteTeams.includes(teamName)) {
        await api.removeFavoriteTeam(teamName);
        setFavoriteTeams(favoriteTeams.filter(t => t !== teamName));
      } else {
        await api.addFavoriteTeam(teamName);
        setFavoriteTeams([...favoriteTeams, teamName]);
      }
      
      if (activeTab === 'favorites') {
        await loadFavoriteGames();
      }
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  // Helper to get the base market type (without _alternate suffix)
  const getBaseMarketType = (marketKey) => {
    return marketKey?.replace('_alternate', '') || marketKey;
  };

  const addToBetSlip = (game, market, outcome) => {
    if (user.is_read_only) {
      showToast('Your account is view-only — betting is disabled', 'error');
      return;
    }
    // Handle futures bets (passed as a single bet object)
    if (game && game.type === 'futures') {
      const futuresBet = game;
      // Format odds for futures
      let formattedOdds;
      if (typeof futuresBet.odds === 'number') {
        formattedOdds = futuresBet.odds > 0 ? `+${futuresBet.odds}` : futuresBet.odds.toString();
      } else {
        formattedOdds = futuresBet.odds;
      }

      const bet = {
        type: 'futures',
        game_id: futuresBet.futuresKey,
        sport_key: futuresBet.sportKey,
        sport_title: futuresBet.sportTitle,
        home_team: futuresBet.selection,
        away_team: '',
        market_type: 'outrights',
        selection: futuresBet.selection,
        odds: formattedOdds,
        point: null,
        description: `${futuresBet.gameDescription}: ${futuresBet.selection}`,
        bet_type: 'futures'
      };

      // Check if already in slip
      const existingIndex = betSlip.findIndex(b =>
        b.game_id === bet.game_id &&
        b.selection === bet.selection
      );

      if (existingIndex >= 0) {
        setBetSlip(betSlip.filter((_, i) => i !== existingIndex));
      } else {
        // Futures cannot be combined into a parlay with anything else (including
        // another futures pick) — they only settle as standalone bets
        // (futures_settler.py only grades standalone Bet rows, never ParlayLeg
        // rows). If the slip already holds anything, start fresh with just
        // this futures pick instead of letting it sit alongside other legs.
        if (betSlip.length > 0) {
          showToast('Futures bets cannot be combined with other picks — starting a new bet slip.', 'error');
          setBetSlip([bet]);
        } else {
          setBetSlip([...betSlip, bet]);
        }
        setShowBetSlip(true);
      }
      return;
    }

    // Regular game bets
    // A futures pick already in the slip can't be combined with a regular
    // pick either — treat the slip as cleared so the new pick starts fresh
    // (use this local copy, not the stale `betSlip` closure, for the rest of
    // this call so the clear actually takes effect before we add to it).
    const hadFutures = betSlip.some(b => b.type === 'futures');
    const currentSlip = hadFutures ? [] : betSlip;
    if (hadFutures) {
      showToast('Futures bets cannot be combined with other picks — starting a new bet slip.', 'error');
    }
    let americanOdds;
    const decimalPrice = outcome.price;

    if (decimalPrice >= 2.0) {
      americanOdds = `+${Math.round((decimalPrice - 1) * 100)}`;
    } else {
      americanOdds = `${Math.round(-100 / (decimalPrice - 1))}`;
    }

    // Determine selection_type for over/under/yes props
    const outcomeName = (outcome.name || '').toLowerCase();
    const descLower = (outcome.description || '').toLowerCase();
    let selectionType = null;
    if (outcomeName === 'over' || outcomeName.startsWith('over ')) {
      // "over" for standard O/U; "Over <Team>" for team totals
      selectionType = 'over';
    } else if (outcomeName === 'under' || outcomeName.startsWith('under ')) {
      selectionType = 'under';
    } else if (outcomeName === 'yes') {
      selectionType = 'yes';
    } else if (descLower === 'over') {
      // DK/Bet365 player props: outcome.name is the player name, description is 'Over'/'Under'
      selectionType = 'over';
    } else if (descLower === 'under') {
      selectionType = 'under';
    } else if (descLower === 'yes') {
      selectionType = 'yes';
    } else if (outcomeName === 'no' || descLower === 'no') {
      // "No" side of a yes/no prop — must be graded as the inverse, not 'yes'
      selectionType = 'no';
    }

    // Extract player name for player props
    // DK/Bet365 format: outcome.name = 'Paolo Banchero', outcome.description = 'Over'/'Under'
    // Legacy format: outcome.description = 'Paolo Banchero Over 25.5'
    // Market key prefixes match organizeGamePropsByCategory's player-market test
    // (baseball uses batter_/pitcher_, not player_, so both must be checked here).
    const isPlayerPropMarket = market.key && (
      market.key.startsWith('player_') || market.key.startsWith('batter_') || market.key.startsWith('pitcher_') ||
      market.key.includes('goal_scorer') || market.key.includes('anytime_scorer')
    );
    let playerName = null;
    if (isPlayerPropMarket) {
      const isDirectionWord = ['over', 'under', 'yes', 'no'].includes(outcomeName);
      if (!isDirectionWord && outcome.name) {
        // Standard DK/Bet365: outcome.name is the player name
        playerName = outcome.name;
      } else {
        // Legacy: extract player name from description (before Over/Under)
        const desc = outcome.description || '';
        playerName = desc.replace(/ Over.*$/i, '').replace(/ Under.*$/i, '').trim() || null;
      }
    }

    // One shared formatter (formatBetSelection) builds this consistently for
    // every screen that shows it — bet slip, pending/past bets, parlay legs.
    const description = (game.sport_key?.startsWith('golf_') && market.key === 'h2h' && game.league)
      ? `${game.away_team} @ ${game.home_team} - ${outcome.name} - ${game.league}`
      : formatBetSelection({
          market_type: market.key,
          selection: outcome.name,
          player_name: playerName,
          selection_type: selectionType,
          point: outcome.point,
          home_team: game.home_team,
          away_team: game.away_team,
        });

    const bet = {
      game_id: game.id,
      sport_key: game.sport_key,
      sport_title: getSportConfig(game.sport_key)?.name || game.sport_key,
      home_team: game.home_team,
      away_team: game.away_team,
      commence_time: game.commence_time,
      market_type: market.key,
      selection: outcome.name,
      selection_type: selectionType,
      player_name: playerName,
      odds: americanOdds,
      point: outcome.point,
      description,
      bet_key: getBetKey(game.id, market.key, outcome),
      bet_type: selectionType || (outcome.name === game.home_team ? 'home' : 'away')
    };

    // Check if this exact bet is already in the slip
    const existingIndex = currentSlip.findIndex(b => b.bet_key === bet.bet_key);

    if (existingIndex >= 0) {
      // Remove if clicking the same bet again (toggle off)
      setBetSlip(currentSlip.filter((_, i) => i !== existingIndex));
    } else {
      // Get player name and base market type for conflict detection
      const playerName = bet.player_name;
      const baseMarketType = getBaseMarketType(bet.market_type);

      // Check for same game + same game market type conflicts (moneyline, spread, total)
      // This prevents parlaying both sides of the same market for the same game
      const gameMarketTypes = ['h2h', 'spreads', 'totals', 'spreads_alternate', 'totals_alternate'];
      const baseGameMarket = bet.market_type.replace('_alternate', '');

      if (gameMarketTypes.includes(bet.market_type) || gameMarketTypes.includes(baseGameMarket)) {
        const conflictIdx = currentSlip.findIndex(b => {
          if (b.game_id !== game.id) return false;
          const existingBaseMarket = b.market_type.replace('_alternate', '');
          return existingBaseMarket === baseGameMarket && ['h2h', 'spreads', 'totals'].includes(baseGameMarket);
        });

        if (conflictIdx >= 0) {
          if (baseGameMarket === 'h2h') {
            // For moneyline / 1x2: replace the existing selection with the new one
            const newSlip = [...currentSlip];
            newSlip[conflictIdx] = bet;
            setBetSlip(newSlip);
            // Don't pop the bet slip open over the Props modal — it just clutters
            // the screen. Add it passively; the floating reopen button still shows.
            if (!selectedGameForProps) setShowBetSlip(true);
            return;
          } else {
            const marketName = baseGameMarket === 'spreads' ? 'spread' : 'total';
            showToast(`You cannot parlay both sides of the ${marketName} for the same game.`, 'error');
            return;
          }
        }
      }

      // Check for same player + same prop type conflicts (including alternates)
      // This prevents betting on both regular and alternate versions of the same stat
      const conflictingBet = currentSlip.find(b => {
        // Different game - no conflict
        if (b.game_id !== game.id) return false;

        const existingPlayer = b.player_name;
        const existingBaseMarket = getBaseMarketType(b.market_type);

        // Same player + same base market type (e.g., player_points and player_points_alternate)
        if (playerName && existingPlayer === playerName && existingBaseMarket === baseMarketType) {
          // If it's the exact same market, only conflict if opposite side (Over/Under)
          if (b.market_type === bet.market_type) {
            return b.description !== bet.description; // Same player, different side
          }
          // Different markets (standard vs alternate) for same prop type - always conflict
          return true;
        }

        return false;
      });

      if (conflictingBet) {
        const isAlternateConflict = conflictingBet.market_type !== bet.market_type;
        if (isAlternateConflict) {
          showToast(`You cannot bet on both standard and alternate ${baseMarketType.replace('player_', '').replace(/_/g, ' ')} for the same player in a parlay.`, 'error');
        } else {
          showToast(`You cannot bet both Over and Under on the same player.`, 'error');
        }
        return;
      }

      // No conflict - add the bet
      setBetSlip([...currentSlip, bet]);
      if (!selectedGameForProps) setShowBetSlip(true);
    }
  };

  const removeBetFromSlip = (index) => {
    setBetSlip(betSlip.filter((_, i) => i !== index));
    if (betSlip.length === 1) {
      setShowBetSlip(false);
    }
  };

  const clearBetSlip = () => {
    setBetSlip([]);
    setBetAmount('');
    setShowBetSlip(false);
    setIsParlayMode(false);
  };

  const calculateParlayOdds = () => {
    if (betSlip.length === 0) return { decimal: 1, american: '+100', payout: 0, profit: 0, capped: false };

    let combined = 1;
    betSlip.forEach(bet => {
      const oddsStr = bet.odds.replace('+', '');
      const odds = parseInt(oddsStr);

      let decimal;
      if (odds > 0) {
        decimal = 1 + (odds / 100);
      } else {
        decimal = 1 + (100 / Math.abs(odds));
      }

      combined *= decimal;
    });

    const amount = parseFloat(betAmount) || 0;
    let payout = amount * combined;
    let capped = false;

    // Apply parlay max payout cap if set (and not 0 which means no limit)
    const maxPayout = user.parlay_max_payout;
    if (maxPayout && maxPayout > 0 && payout > maxPayout && isParlayMode && betSlip.length >= 2) {
      payout = maxPayout;
      capped = true;
    }

    const profit = payout - amount;

    let american;
    if (combined >= 2.0) {
      american = `+${Math.round((combined - 1) * 100)}`;
    } else {
      american = `${Math.round(-100 / (combined - 1))}`;
    }

    return {
      decimal: combined.toFixed(2),
      american,
      payout: payout.toFixed(2),
      profit: profit.toFixed(2),
      capped
    };
  };

  const placeBet = async () => {
    if (user.is_read_only) {
      showToast('Your account is view-only — betting is disabled', 'error');
      return;
    }
    if (!betAmount || parseFloat(betAmount) <= 0) {
      showToast('Please enter a valid bet amount', 'error');
      return;
    }

    if (betSlip.length === 0) {
      showToast('Please add at least one bet to your slip', 'error');
      return;
    }

    const amount = parseFloat(betAmount);
    // Use parlay limits when in parlay mode with 2+ legs
    const isParlay = isParlayMode && betSlip.length >= 2;
    const minBet = isParlay ? (user.parlay_min_bet || user.min_bet) : user.min_bet;
    const maxBet = isParlay ? (user.parlay_max_bet || user.max_bet) : user.max_bet;

    if (amount < minBet) {
      showToast(`Minimum ${isParlay ? 'parlay ' : ''}bet is $${minBet}`, 'error');
      return;
    }
    // maxBet = 0 means no limit
    if (maxBet > 0 && amount > maxBet) {
      showToast(`Maximum ${isParlay ? 'parlay ' : ''}bet is $${maxBet}`, 'error');
      return;
    }

    // For multiple singles, check total cost against balance
    const totalCost = betSlip.length > 1 && !isParlayMode ? amount * betSlip.length : amount;

    // Check balance based on whether using free play
    if (useFreePlay) {
      if (totalCost > freePlayBalance) {
        showToast(`Insufficient free play balance. Need ${totalCost.toFixed(2)} ₵₵, have ${freePlayBalance.toFixed(2)} ₵₵`, 'error');
        return;
      }
    } else {
      if (totalCost > availableBalance) {
        showToast(`Insufficient credit. Need ${totalCost.toFixed(2)} ₵₵, have ${availableBalance.toFixed(2)} ₵₵`, 'error');
        return;
      }
    }

    // Multiple singles — show confirmation modal instead of placing immediately
    if (betSlip.length >= 2 && !isParlayMode) {
      setShowBetSlip(false); // close the slip so it doesn't sit behind the confirm modal
      setShowMultiBetModal(true);
      return;
    }

    try {
      if (isParlayMode && betSlip.length >= 2) {
        await api.placeParlay({
          amount: amount,
          legs: betSlip,
          use_free_play: useFreePlay
        });
        showToast(`Parlay placed successfully${useFreePlay ? ' (Free Play)' : ''}!`);
      } else if (betSlip.length === 1) {
        const bet = betSlip[0];
        // Build description based on bet type
        let betDescription;
        if (bet.type === 'futures') {
          betDescription = bet.description;
        } else {
          betDescription = `${bet.away_team} @ ${bet.home_team} - ${bet.description}`;
        }
        await api.placeBet({
          amount: amount,
          bet_type: bet.sport_title,
          market_type: bet.market_type,  // h2h, spreads, totals, player_points, outrights etc.
          description: betDescription,
          odds: bet.odds,
          game_id: bet.game_id,
          sport_key: bet.sport_key,
          commence_time: bet.commence_time,  // scheduled start — ESPN game resolution
          use_free_play: useFreePlay,
          // Critical fields for auto-settlement of player props
          selection_type: bet.selection_type,  // 'over', 'under', 'yes'
          player_name: bet.player_name,
          name: bet.selection,  // The outcome name (e.g., "Over", "Under")
          point: bet.point  // The line value
        });
        showToast(`Bet placed successfully${useFreePlay ? ' (Free Play)' : ''}!`);
      }

      clearBetSlip();
      setUseFreePlay(false);
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to place bet', 'error');
    }
  };

  const confirmMultipleSingles = async () => {
    const amount = parseFloat(betAmount);
    setShowMultiBetModal(false);
    try {
      const betsPayload = betSlip.map(bet => {
        let betDescription;
        if (bet.type === 'futures') {
          betDescription = bet.description;
        } else {
          betDescription = `${bet.away_team} @ ${bet.home_team} - ${bet.description}`;
        }
        return {
          amount,
          bet_type: bet.sport_title,
          market_type: bet.market_type,
          description: betDescription,
          odds: bet.odds,
          game_id: bet.game_id,
          sport_key: bet.sport_key,
          commence_time: bet.commence_time,  // scheduled start — ESPN game resolution
          selection_type: bet.selection_type,
          player_name: bet.player_name,
          name: bet.selection,
          point: bet.point
        };
      });
      await api.placeMultipleSingles({ amount, bets: betsPayload, use_free_play: useFreePlay });
      showToast(`${betSlip.length} bets placed successfully${useFreePlay ? ' (Free Play)' : ''}!`);
      clearBetSlip();
      setUseFreePlay(false);
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to place bets', 'error');
    }
  };

  const getFilteredGames = () => {
    if (searchedGames && searchedGames.length > 0) {
      return searchedGames;
    }
    if (searchedGame) {
      return [searchedGame];
    }
    if (activeSport === 'all') {
      return Object.values(games).flat();
    }
    const sportGames = games[activeSport] || [];
    const leagueSet = new Set(sportGames.map(g => g.league || '').filter(Boolean));
    if (leagueSet.size <= 1) {
      return sportGames;
    }

    const groups = groupLeaguesByPrefix(Array.from(leagueSet));
    if (shouldGroupLeagues(groups, leagueSet.size)) {
      // Group selected: show every league in that group unless a specific
      // one has also been picked (the level-2 chip row).
      const group = activeLeagueGroup || Array.from(groups.keys()).sort()[0];
      if (activeLeagueFilter) {
        return sportGames.filter(g => (g.league || '') === activeLeagueFilter);
      }
      return sportGames.filter(g => splitLeagueName(g.league || '').group === group);
    }

    const filter = activeLeagueFilter || Array.from(leagueSet).sort()[0];
    return sportGames.filter(g => (g.league || '') === filter);
  };

  const betCalc = isParlayMode && betSlip.length >= 2 ? calculateParlayOdds() : null;

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: colors.bgGradient, fontFamily: 'Nunito, sans-serif' }}>
      <div className="text-xl" style={{ color: colors.textPrimary }}>Loading...</div>
    </div>;
  }

  return (
    <div className="min-h-screen flex flex-col pb-14" style={{ fontFamily, color: colors.textPrimary }}>
      <div className="fixed inset-0 -z-10" style={{ background: colors.bgGradient }} />
      <nav className="px-3 py-1.5 flex items-center justify-between" style={{ background: 'rgba(0,0,0,0.4)' }}>
        <img src={CakeStakesText} alt="CakeStakes" className="h-8 md:h-11 w-auto object-contain" style={{ imageRendering: 'crisp-edges' }} />
        <div className="flex items-center gap-2">
          <span className="text-sm hidden sm:block">Hi, <strong>{user.name}</strong></span>
          <button
            onClick={() => setActiveTab(activeTab === 'support' ? 'home' : 'support')}
            className="relative px-3 py-1.5 rounded-lg text-sm font-medium"
            style={{ background: colors.goldActiveBg, color: colors.textPrimary }}
          >
            {activeTab === 'support' ? 'Back' : 'Support'}
            {unreadCount > 0 && activeTab !== 'support' && (
              <span className="absolute -top-1 -right-1 rounded-full w-4 h-4 flex items-center justify-center text-[10px]" style={{ background: colors.negative }}>{unreadCount}</span>
            )}
          </button>
          <button onClick={onLogout} className="px-3 py-1.5 rounded-lg text-sm font-medium" style={{ background: colors.negative, color: colors.textPrimary }}>
            Logout
          </button>
        </div>
      </nav>

      {activeTab !== 'support' && (
        <div className="px-3 py-1">
          <div className="rounded-lg px-3 py-1.5" style={{ background: colors.sectionBg }}>
            <p className="text-[12px] font-medium" style={{ color: colors.textPrimary }}>Credit</p>
            <p className="text-xl md:text-2xl font-bold" style={{ color: colors.odds }}>{availableBalance.toFixed(2)}<CC /></p>
            <p className="text-[11px] font-medium mt-1" style={{ color: colors.textPrimary }}>Free Play</p>
            <p className="text-sm md:text-base font-bold" style={{ color: colors.freePlay }}>{freePlayBalance.toFixed(2)}<CC /></p>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto pb-20 md:pb-6">
        {activeTab === 'support' && (
          <div className="p-2 md:p-4 lg:p-6 max-w-7xl mx-auto">
            <SupportView
              messages={messages}
              selectedMessage={selectedMessage}
              messageView={messageView}
              onViewChange={(view) => { setMessageView(view); loadMessages(); }}
              onOpenMessage={openMessage}
              onSendNew={() => setShowMessageModal(true)}
              onReply={replyToMessage}
              onLoadMessages={loadMessages}
              onBack={() => { setSelectedMessage(null); loadMessages(); }}
            />
          </div>
        )}

        {activeTab === 'home' && (
          <HomeTab
            enabledSports={enabledSports}
            onSelectCategory={toggleCategory}
            activeTab={activeTab}
            activeSport={activeSport}
            onSelectSport={(key) => { if (key === 'futures-shortcut') { setActiveTab('futures'); } else { setActiveSport(key); } }}
            games={games}
            activeLeaguesData={activeLeaguesData}
            activeLeagueFilter={activeLeagueFilter}
            onSelectLeagueFilter={setActiveLeagueFilter}
            activeLeagueGroup={activeLeagueGroup}
            onSelectLeagueGroup={(group) => { setActiveLeagueGroup(group); setActiveLeagueFilter(null); }}
            onDynamicLeagueClick={handleDynamicLeagueClick}
            teamSearchQuery={teamSearchQuery}
            onSearchChange={searchTeams}
            searchResults={searchResults}
            onSelectSearchResult={selectSearchResult}
            onShowAllResults={showAllSearchResults}
            onClearSearch={clearSearch}
            searchedGame={searchedGame}
            searchedGamesCount={searchedGames?.length || 0}
            onClearSearchedGame={() => { setSearchedGame(null); setSearchedGames(null); }}
            betSlip={betSlip}
            favoriteTeams={favoriteTeams}
            onAddToBetSlip={addToBetSlip}
            onLoadProps={loadAllProps}
            onToggleFavorite={toggleFavoriteTeam}
            getFilteredGames={getFilteredGames}
          />
        )}

        {activeTab === 'live' && (
          <LiveTab
            liveSports={liveSports}
            activeLiveSport={activeLiveSport}
            onSelectLiveSport={setActiveLiveSport}
            liveOdds={liveOdds}
            liveBetSlip={liveBetSlip}
            showLiveBetSlip={showLiveBetSlip}
            onAddToLiveBetSlip={addToLiveBetSlip}
            onUpdateLiveBetSlip={setLiveBetSlip}
            onRemoveLiveBet={(idx) => setLiveBetSlip(prev => prev.filter((_, i) => i !== idx))}
            onClearLiveBetSlip={() => setLiveBetSlip([])}
            onCloseLiveBetSlip={() => setShowLiveBetSlip(false)}
            freePlayBalance={freePlayBalance}
            onBetPlaced={loadData}
            user={user}
            showToast={showToast}
            favoriteTeams={favoriteTeams}
            onToggleFavorite={toggleFavoriteTeam}
            onLoadProps={loadAllProps}
            activeLeagueGroup={activeLeagueGroup}
            onSelectLeagueGroup={(group) => { setActiveLeagueGroup(group); setActiveLeagueFilter(null); }}
            activeLeagueFilter={activeLeagueFilter}
            onSelectLeagueFilter={setActiveLeagueFilter}
          />
        )}

        {activeTab === 'futures' && (
          <FuturesTab
            futuresData={futuresData}
            futuresLoading={futuresLoading}
            activeFuturesLeague={activeFuturesLeague}
            onSelectLeague={setActiveFuturesLeague}
            betSlip={betSlip}
            onAddToBetSlip={addToBetSlip}
          />
        )}

        {activeTab === 'favorites' && (
          <FavoritesTab
            favoriteGames={favoriteGames}
            favoriteTeams={favoriteTeams}
            betSlip={betSlip}
            onAddToBetSlip={addToBetSlip}
            onLoadProps={loadAllProps}
            onToggleFavorite={toggleFavoriteTeam}
          />
        )}

        {activeTab === 'mybets' && (
          <MyBetsTab
            weeklyBalance={weeklyBalance}
            pendingTotal={pendingTotal}
            betHistoryTab={betHistoryTab}
            onChangeBetHistoryTab={setBetHistoryTab}
            betHistoryPeriod={betHistoryPeriod}
            onChangePeriod={(period) => { setBetHistoryPeriod(period); loadBetHistory(period); loadLiveBetHistory(); }}
            pendingBets={myBets}
            pendingParlays={myParlays}
            pendingLiveBets={pendingLiveBets}
            pendingLiveParlays={pendingLiveParlays}
            pastBets={pastBets}
            pastParlays={pastParlays}
            loading={loadingBetHistory}
            user={user}
            onCashout={setCashoutParlay}
            onCashoutBet={setCashoutBet}
            onCashoutLiveBet={setCashoutLiveBet}
            onCashoutLiveParlay={setCashoutLiveParlay}
          />
        )}

        {activeTab === 'casino' && (
          <CasinoTab
            showToast={showToast}
            onGameClose={() => { loadData(); loadBetHistory(betHistoryPeriod); loadLiveBetHistory(); }}
          />
        )}
        {activeTab === 'leaderboard' && (
          <LeaderboardTab />
        )}
      </div>

      {activeTab !== 'support' && (
        <BottomNav
          activeTab={activeTab}
          onTabChange={setActiveTab}
          user={user}
        />
      )}

      {/* Small self-dismissing notification — replaces browser alert() popups */}
      {toast && (
        <div
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[100] px-4 py-3 rounded-lg shadow-lg max-w-[90vw] text-center text-sm font-medium"
          style={{
            background: 'rgba(20,5,25,0.97)',
            boxShadow: colors.shadowRaised,
            color: colors.textPrimary,
            border: `1px solid ${toast.type === 'error' ? colors.negative : colors.odds}`,
          }}
        >
          {toast.message}
        </div>
      )}

      {/* Floating Bet Slip Button (Mobile) */}
      {betSlip.length > 0 && !showBetSlip && activeTab !== 'support' && activeTab !== 'live' && (
        <button
          onClick={() => { closeProps(); setShowBetSlip(true); }}
          className="fixed bottom-20 right-4 px-4 py-3 rounded-full shadow-lg flex items-center gap-2 z-[61]" style={{ background: '#BF953F', color: '#200e22' }}
        >
          <span className="font-bold">{betSlip.length}</span>
          <span className="text-sm">Bet Slip</span>
        </button>
      )}

      {/* Bet Slip - Floating panel that grows with content */}
      {showBetSlip && activeTab !== 'support' && activeTab !== 'live' && (
        <div className="fixed bottom-20 right-4 w-96 lg:w-[28rem] z-[60] flex flex-col rounded-t-2xl md:rounded-lg shadow-2xl" style={{ background: 'rgba(20,5,25,0.97)', boxShadow: colors.shadowRaised, maxHeight: '80vh' }}>
            {/* Header */}
            <div className="flex items-center justify-between p-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.3)' }}>
              <h3 className="text-lg font-bold" style={{ color: colors.textPrimary }}>Bet Slip ({betSlip.length})</h3>
              <button
                onClick={() => setShowBetSlip(false)}
                className="p-1" style={{ color: colors.textMuted }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {betSlip.length === 0 ? (
              <p className="text-center py-8 text-sm flex-1 flex items-center justify-center" style={{ color: colors.textFaint }}>
                Click odds to add bets
              </p>
            ) : (
              <>
                {/* Scrollable bets list */}
                <div className="flex-1 overflow-y-auto p-3 space-y-2">
                  {betSlip.map((bet, idx) => (
                    <div key={idx} className="rounded-lg p-2" style={{ background: 'rgba(0,0,0,0.3)', boxShadow: colors.shadowSubtle }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] truncate" style={{ color: colors.textFaint }}>
                            {bet.type === 'futures' ? (
                              <>🎯 {bet.sport_title}</>
                            ) : (
                              <>{SPORTS_CONFIG[bet.sport_key]?.icon} {bet.away_team} @ {bet.home_team}</>
                            )}
                          </div>
                          <div className="font-semibold text-xs truncate" style={{ color: colors.textPrimary }}>
                            {bet.description}
                          </div>
                          <div className="text-xs font-medium" style={{ color: colors.odds }}>
                            {bet.odds}
                          </div>
                        </div>
                        <button
                          onClick={() => removeBetFromSlip(idx)}
                          className="text-red-500 hover:text-red-700 ml-2 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer - fixed at bottom */}
                <div className="p-3 flex-shrink-0 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}>
                  {/* addToBetSlip already keeps a futures pick from ever sharing the
                      slip with anything else, but the length check alone can't tell —
                      guard the parlay toggle explicitly too so it can never be offered
                      for a futures pick (futures only settle as standalone bets). */}
                  {betSlip.length >= 2 && !betSlip.some(b => b.type === 'futures') && (
                    <div className="p-2 rounded-lg" style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)' }}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isParlayMode}
                          onChange={(e) => setIsParlayMode(e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-xs font-medium" style={{ color: '#d8b4fe' }}>
                          {betSlip.length}-Leg Parlay
                        </span>
                        {isParlayMode && betCalc && (
                          <span className="text-xs ml-auto" style={{ color: '#c084fc' }}>
                            {betCalc.american}
                          </span>
                        )}
                      </label>
                    </div>
                  )}

                  <div className="flex gap-2 items-center">
                    <div className="flex-1">
                      <input
                        type="number"
                        value={betAmount}
                        onChange={(e) => setBetAmount(e.target.value)}
                        placeholder={`${isParlayMode && betSlip.length >= 2 ? (user.parlay_min_bet || user.min_bet) : user.min_bet} ₵₵ - ${isParlayMode && betSlip.length >= 2 ? (user.parlay_max_bet || user.max_bet) : user.max_bet} ₵₵`}
                        min={isParlayMode && betSlip.length >= 2 ? (user.parlay_min_bet || user.min_bet) : user.min_bet}
                        max={isParlayMode && betSlip.length >= 2 ? (user.parlay_max_bet || user.max_bet) : user.max_bet}
                        className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
                        style={{ background: 'rgba(0,0,0,0.3)', boxShadow: colors.shadowCard, color: colors.textPrimary }}
                      />
                    </div>
                    {betAmount && parseFloat(betAmount) > 0 && (
                      <div className="text-right">
                        <div className="text-[10px]" style={{ color: colors.textFaint }}>To Win</div>
                        <div className="text-sm font-bold" style={{ color: '#4ade80' }}>
                          {(() => {
                            if (isParlayMode && betCalc) {
                              return betCalc.profit;
                            } else if (betSlip.length === 1) {
                              const amount = parseFloat(betAmount);
                              const oddsStr = betSlip[0].odds.replace('+', '');
                              const odds = parseInt(oddsStr);

                              let profit;
                              if (odds > 0) {
                                profit = amount * (odds / 100);
                              } else {
                                profit = amount * (100 / Math.abs(odds));
                              }
                              return profit.toFixed(2);
                            }
                            return '0.00';
                          })()}<CC />
                        </div>
                      </div>
                    )}
                  </div>

                  {freePlayBalance > 0 && (
                    <div className="flex items-center justify-between p-2 rounded" style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)' }}>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={useFreePlay}
                          onChange={(e) => setUseFreePlay(e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-xs font-medium" style={{ color: '#d8b4fe' }}>Free Play</span>
                      </label>
                      <span className="text-xs font-bold" style={{ color: '#c084fc' }}>{freePlayBalance.toFixed(2)}<CC /></span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={clearBetSlip}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition" style={{ background: 'rgba(255,255,255,0.1)', color: colors.textPrimary, boxShadow: colors.shadowCard }}
                    >
                      Clear
                    </button>
                    <button
                      onClick={placeBet}
                      disabled={user.is_read_only}
                      className={`flex-1 py-2 rounded-lg font-bold transition text-sm ${
                        user.is_read_only
                          ? 'bg-gray-600 text-gray-300 cursor-not-allowed'
                          : useFreePlay
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white'
                          : 'bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white'
                      }`}
                    >
                      Place {isParlayMode && betSlip.length >= 2 ? 'Parlay' : 'Bet'}{useFreePlay ? ' (FP)' : ''}
                    </button>
                  </div>
                </div>
              </>
            )}
        </div>
      )}

      {selectedGameForProps && (() => {
        const mergedMarkets = [
          ...(playerProps?.bookmakers?.[0]?.markets || []),
          ...(gameProps?.bookmakers?.[0]?.markets || []),
        ];
        return (
        <GamePropsModal
          game={selectedGameForProps}
          props={mergedMarkets.length > 0 ? { bookmakers: [{ markets: mergedMarkets }] } : null}
          loading={loadingProps || loadingGameProps}
          title="Props"
          onClose={closeProps}
          onAddToBetSlip={addToBetSlip}
          betSlip={betSlip}
        />
        );
      })()}
      {showMessageModal && (
        <NewMessageModal
          onClose={() => setShowMessageModal(false)}
          onSend={sendNewMessage}
        />
      )}
      {showMultiBetModal && (
        <MultiBetConfirmModal
          bets={betSlip}
          amount={parseFloat(betAmount)}
          useFreePlay={useFreePlay}
          onConfirm={confirmMultipleSingles}
          onCancel={() => { setShowMultiBetModal(false); setShowBetSlip(true); }}
        />
      )}
      {(cashoutParlay || cashoutBet || cashoutLiveBet || cashoutLiveParlay) && (
        <CashoutModal
          betType={cashoutParlay ? 'parlay' : cashoutBet ? 'single' : cashoutLiveBet ? 'live_single' : 'live_parlay'}
          wager={cashoutParlay || cashoutBet || cashoutLiveBet || cashoutLiveParlay}
          onClose={() => { setCashoutParlay(null); setCashoutBet(null); setCashoutLiveBet(null); setCashoutLiveParlay(null); }}
          onCashoutSuccess={async (result) => {
            // Refresh bet history, pending bets, and account balance
            loadBetHistory();
            loadLiveBetHistory();
            try {
              const [betsData, accountData] = await Promise.all([
                api.getBetHistory(user.id, 'pending'),
                api.getAccount(user.id)
              ]);
              const singleBets = betsData.single_bets || [];
              const parlays = betsData.parlays || [];
              setMyBets(singleBets);
              setMyParlays(parlays);
              const singlePending = singleBets.reduce((sum, bet) => sum + bet.amount, 0);
              const parlayPending = parlays.reduce((sum, parlay) => sum + parlay.amount, 0);
              setPendingTotal(singlePending + parlayPending);
              const account = accountData.user;
              setAvailableBalance(account.available_credit || 0);
              setWeeklyBalance(account.weekly_balance || 0);
            } catch (err) {
              console.error('Error refreshing after cashout:', err);
            }
            setCashoutParlay(null);
            setCashoutBet(null);
            setCashoutLiveBet(null);
            setCashoutLiveParlay(null);
          }}
        />
      )}
    </div>
  );
}

function PlayerPropsModal({ game, props, loading, onClose, onAddToBetSlip, betSlip }) {
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const [loadedCategories, setLoadedCategories] = React.useState({});
  const [loadingCategory, setLoadingCategory] = React.useState(false);
  const [expandedAlternates, setExpandedAlternates] = React.useState({});
  const [expandedAltCategories, setExpandedAltCategories] = React.useState({});

  const toggleAlternateExpand = (propName, playerName) => {
    const key = `${propName}-${playerName}`;
    setExpandedAlternates(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleAltCategoryExpand = (categoryName) => {
    setExpandedAltCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const isInBetSlip = (market, outcome) => {
    const outcomeDescLower = (outcome.description || '').toLowerCase();
    return betSlip.some(bet =>
      bet.game_id === game.id &&
      bet.market_type === market.key &&
      (bet.player_name === outcome.name || bet.selection === outcome.name) &&
      (bet.selection_type === outcomeDescLower || bet.bet_type === outcomeDescLower)
    );
  };

  // Check if odds are valid (not infinity, not too extreme)
  const isValidOdds = (price) => {
    if (typeof price !== 'number') return false;
    if (!isFinite(price)) return false;
    if (price < 1.01) return false;
    if (price > 10000) return false;
    return true;
  };

  const formatOdds = (price) => {
    if (typeof price === 'string') return price;
    if (typeof price === 'number') {
      if (!isValidOdds(price)) return null;
      if (price >= 2.0) {
        return `+${Math.round((price - 1) * 100)}`;
      } else {
        return `${Math.round(-100 / (price - 1))}`;
      }
    }
    return price;
  };

  // Filter outcomes to only include those with valid odds
  const filterValidOutcomes = (outcomes) => {
    if (!outcomes) return [];
    return outcomes.filter(o => isValidOdds(o.price));
  };

  // Group prop types into categories - sport specific
  const getCategoryGroups = () => {
    const sportKey = game.sport_key;

    // Basketball categories (NBA, NCAAB, WNBA)
    if (sportKey?.startsWith('basketball_')) {
      return {
        '🎯 Scoring':     ['player_points', 'player_points_alternate', 'player_points_q1', 'player_threes', 'player_threes_alternate', 'player_shots', 'player_field_goals', 'player_frees_made', 'player_frees_attempts'],
        '🏀 Rebounds':    ['player_rebounds', 'player_rebounds_alternate', 'player_rebounds_q1'],
        '🤝 Assists':     ['player_assists', 'player_assists_alternate', 'player_assists_q1'],
        '🔀 Combo Stats': ['player_points_rebounds_assists', 'player_points_rebounds_assists_alternate', 'player_points_rebounds', 'player_points_rebounds_alternate', 'player_points_assists', 'player_points_assists_alternate', 'player_rebounds_assists', 'player_rebounds_assists_alternate', 'player_passes'],
        '🛡️ Defense':    ['player_blocks', 'player_blocks_alternate', 'player_steals', 'player_steals_alternate', 'player_blocks_steals', 'player_turnovers', 'player_turnovers_alternate'],
        '⭐ Specials':    ['player_double_double', 'player_triple_double', 'player_first_basket', 'player_first_team_basket', 'player_method_of_first_basket']
      };
    }

    // NHL/Hockey categories
    if (sportKey === 'icehockey_nhl') {
      return {
        '🏒 Scoring':      ['player_points', 'player_points_alternate', 'player_goals', 'player_goals_alternate', 'player_assists', 'player_assists_alternate'],
        '🎯 Shots':        ['player_shots_on_goal', 'player_shots_on_goal_alternate', 'player_blocked_shots', 'player_blocked_shots_alternate'],
        '🥅 Goalie':       ['player_total_saves', 'player_total_saves_alternate'],
        '⚡ Power Play':   ['player_power_play_points', 'player_power_play_points_alternate'],
        '🏆 Goal Scorers': ['player_goal_scorer_anytime', 'player_goal_scorer_first', 'player_goal_scorer_last']
      };
    }

    // MLB/Baseball categories
    if (sportKey === 'baseball_mlb') {
      return {
        '⚾ Pitching': ['pitcher_strikeouts', 'pitcher_strikeouts_alternate', 'pitcher_outs', 'pitcher_hits_allowed', 'pitcher_hits_allowed_alternate', 'pitcher_earned_runs', 'pitcher_walks', 'pitcher_walks_alternate', 'pitcher_record_a_win'],
        '🏏 Hitting':  ['batter_hits', 'batter_hits_alternate', 'batter_total_bases', 'batter_total_bases_alternate', 'batter_runs_scored', 'batter_runs_scored_alternate'],
        '💪 Power':    ['batter_home_runs', 'batter_home_runs_alternate', 'batter_rbis', 'batter_rbis_alternate', 'batter_first_home_run'],
        '⚡ Bases':    ['batter_singles', 'batter_singles_alternate', 'batter_doubles', 'batter_doubles_alternate', 'batter_triples', 'batter_triples_alternate', 'batter_stolen_bases'],
        '📊 Misc':     ['batter_walks', 'batter_walks_alternate', 'batter_strikeouts', 'batter_strikeouts_alternate', 'batter_hits_runs_rbis'],
      };
    }

    // Soccer – scorers (limited player props available)
    if (sportKey?.startsWith('soccer_')) {
      return {
        '⚽ Goal Scorers': ['player_goal_scorer_anytime', 'player_goal_scorer_first', 'player_goal_scorer_last'],
      };
    }

    // Tennis – no meaningful player props (match props are in game props)
    if (sportKey?.startsWith('tennis_')) {
      return {};
    }

    // Combat Sports (MMA/Boxing) – no player props (all fight props in game props)
    if (sportKey?.startsWith('mma_') || sportKey?.startsWith('boxing_')) {
      return {};
    }

    // Golf – no player props (outrights are in game props)
    if (sportKey?.startsWith('golf_')) {
      return {};
    }

    // New sports – no player props, all markets in game props
    if (
      sportKey?.startsWith('handball_') ||
      sportKey?.startsWith('volleyball_') ||
      sportKey?.startsWith('esports_') ||
      sportKey?.startsWith('tabletennis_') ||
      sportKey?.startsWith('darts_') ||
      sportKey?.startsWith('futsal_') ||
      sportKey?.startsWith('rugbyleague_') ||
      sportKey?.startsWith('rugbyunion_') ||
      sportKey?.startsWith('cricket_')
    ) {
      return {};
    }

    // NFL/Football categories (default)
    return {
      '🏆 Touchdowns': ['player_anytime_td', 'player_1st_td', 'player_last_td', 'player_tds_over'],
      '🏈 Passing':    ['player_pass_yds', 'player_pass_yds_alternate', 'player_pass_tds', 'player_pass_tds_alternate', 'player_pass_completions', 'player_pass_completions_alternate', 'player_pass_attempts', 'player_pass_attempts_alternate', 'player_pass_interceptions', 'player_pass_interceptions_alternate', 'player_pass_longest_completion', 'player_pass_longest_completion_alternate', 'player_pass_yds_q1'],
      '🏃 Rushing':    ['player_rush_yds', 'player_rush_yds_alternate', 'player_rush_tds', 'player_rush_tds_alternate', 'player_rush_attempts', 'player_rush_attempts_alternate', 'player_rush_longest', 'player_rush_longest_alternate'],
      '🙌 Receiving':  ['player_receptions', 'player_receptions_alternate', 'player_reception_yds', 'player_reception_yds_alternate', 'player_reception_tds', 'player_reception_tds_alternate', 'player_reception_longest', 'player_reception_longest_alternate'],
      '🔀 Combo Stats':['player_pass_rush_yds', 'player_pass_rush_yds_alternate', 'player_rush_reception_yds', 'player_rush_reception_yds_alternate', 'player_rush_reception_tds', 'player_rush_reception_tds_alternate', 'player_pass_rush_reception_yds', 'player_pass_rush_reception_yds_alternate', 'player_pass_rush_reception_tds', 'player_pass_rush_reception_tds_alternate'],
      '🛡️ Defense':   ['player_sacks', 'player_sacks_alternate', 'player_tackles_assists', 'player_tackles_assists_alternate', 'player_solo_tackles', 'player_solo_tackles_alternate', 'player_defensive_interceptions', 'player_assists', 'player_assists_alternate'],
      '👟 Kicking':    ['player_field_goals', 'player_field_goals_alternate', 'player_kicking_points', 'player_kicking_points_alternate', 'player_pats', 'player_pats_alternate'],
    };
  };

  const loadCategoryProps = async (categoryName) => {
    if (loadedCategories[categoryName]) {
      setSelectedCategory(categoryName);
      return;
    }

    setLoadingCategory(true);
    setSelectedCategory(categoryName);

    try {
      const categoryMarkets = getCategoryGroups()[categoryName];
      const marketsStr = categoryMarkets.join(',');
      
      // Use the api.js method instead of raw fetch
      const data = await api.getPlayerProps(game.sport_key, game.id, marketsStr);
      
      if (data.props) {
        setLoadedCategories(prev => ({
          ...prev,
          [categoryName]: data.props
        }));
      }
    } catch (err) {
      console.error('Failed to load category props:', err);
    } finally {
      setLoadingCategory(false);
    }
  };

  const organizePropsByCategory = (propsData) => {
    if (!propsData?.bookmakers?.[0]?.markets) return {};

    const organized = {};

    // Yes/No prop detection patterns
    const yesNoPatterns = [
      '_td', 'tds_over',  // Football touchdowns
      'double_double', 'triple_double',  // Basketball doubles
      'first_basket', 'first_team_basket', 'method_of_first_basket',  // Basketball first basket
      'first_assist', 'first_rebound',  // Basketball first-player props
      'goal_scorer_anytime', 'goal_scorer_first', 'goal_scorer_last',  // Hockey goal scorers (GoalServe)
      'anytime_goalscorer',  // Hockey/Soccer anytime scorer (odds-api.io Bet365)
      'to_be_booked', 'to_score_or_assist', 'fouled',  // Soccer yes/no props
      'batter_first_home_run', 'pitcher_record_a_win'  // Baseball yes/no props
    ];

    propsData.bookmakers[0].markets.forEach(market => {
      const categoryName = PROP_DISPLAY_NAMES[market.key] || market.key;
      const isAlternate = market.key.includes('_alternate');
      const isYesNoProp = yesNoPatterns.some(pattern => market.key.includes(pattern));

      if (!organized[categoryName]) {
        organized[categoryName] = {
          marketKey: market.key,
          isAlternate: isAlternate,
          isYesNoProp: isYesNoProp,
          categoryLabel: categoryName,
          players: {},
          seenOutcomes: {}  // Track seen outcomes to prevent true duplicates
        };
      }

      const category = organized[categoryName];

      market.outcomes.forEach(outcome => {
        // Determine the player name.
        // odds-api.io (DK/Bet365): name = player name, description = 'Over'|'Under'
        // GoalServe: name = player name, description = undefined
        // Legacy format: description = 'Player Name Over' (strip the side to get name)
        const desc = outcome.description || '';
        const isOverUnderDesc = desc === 'Over' || desc === 'Under';
        const playerName = isOverUnderDesc
          ? outcome.name
          : (desc.replace(' Over', '').replace(' Under', '').trim() || outcome.name);

        if (!category.players[playerName]) {
          category.players[playerName] = [];
        }

        // Create unique key for deduplication: player + point + side + odds
        // Use description for side when it's Over/Under, else use name (GoalServe format)
        const side = isOverUnderDesc ? desc.toLowerCase() : (outcome.name?.toLowerCase() || '');
        const point = outcome.point !== undefined ? outcome.point : '';
        const price = outcome.price || '';
        const uniqueKey = `${playerName}|${point}|${side}|${price}`;

        // Skip true duplicate outcomes (same player, point, side, AND odds)
        if (category.seenOutcomes[uniqueKey]) {
          return;
        }
        category.seenOutcomes[uniqueKey] = true;

        // For alternates, each outcome is a separate option
        // For regular over/under, group by player
        category.players[playerName].push({
          ...outcome,
          market: { key: market.key }
        });
      });
    });

    // Clean up the seenOutcomes tracking (not needed in output)
    Object.values(organized).forEach(cat => delete cat.seenOutcomes);

    return organized;
  };

  const renderAlternateProp = (player, outcomes, category, propName) => {
    // Filter out invalid odds (e.g., -infinity for settled live bets)
    const validOutcomes = filterValidOutcomes(outcomes);
    if (validOutcomes.length === 0) return null;

    // Group outcomes by point value, separating Over and Under
    const lineGroups = {};
    validOutcomes.forEach(outcome => {
      const point = outcome.point;
      if (!lineGroups[point]) {
        lineGroups[point] = { over: null, under: null };
      }
      const descLower = (outcome.description || outcome.name || '').toLowerCase();
      if (descLower === 'over' || descLower.includes('over')) {
        lineGroups[point].over = outcome;
      } else if (descLower === 'under' || descLower.includes('under')) {
        lineGroups[point].under = outcome;
      } else {
        // If not labeled, assume first is Over (usually negative odds for lower lines)
        if (!lineGroups[point].over) {
          lineGroups[point].over = outcome;
        } else {
          lineGroups[point].under = outcome;
        }
      }
    });

    // Sort point values
    const sortedPoints = Object.keys(lineGroups).map(Number).sort((a, b) => a - b);
    const expandKey = `${propName}-${player}`;
    const isExpanded = expandedAlternates[expandKey];

    // Check if any outcome for this player is in the bet slip
    const hasSelectionInSlip = validOutcomes.some(outcome => isInBetSlip({ key: category.marketKey }, outcome));

    // Get range of lines for collapsed view
    const minLine = sortedPoints[0];
    const maxLine = sortedPoints[sortedPoints.length - 1];

    return (
      <div className="rounded-lg mb-3 overflow-hidden" style={{ background: 'rgba(0,0,0,0.25)', boxShadow: colors.shadowCard }}>
        {/* Collapsible Header */}
        <button
          onClick={() => toggleAlternateExpand(propName, player)}
          className={`w-full p-3 flex items-center justify-between transition`}
          style={hasSelectionInSlip ? { background: colors.goldActiveBg } : {}}
        >
          <div className="flex items-center gap-3">
            <span className="font-semibold text-sm" style={{ color: colors.textPrimary }}>{player}</span>
            {hasSelectionInSlip && (
              <span className="text-xs px-2 py-0.5 rounded" style={{ background: '#BF953F', color: '#200e22' }}>In Slip</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: colors.textMuted }}>
              {sortedPoints.length} lines ({minLine} - {maxLine})
            </span>
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" style={{ color: colors.textMuted }} />
            ) : (
              <ChevronRight className="w-4 h-4" style={{ color: colors.textMuted }} />
            )}
          </div>
        </button>

        {/* Expandable Content */}
        {isExpanded && (
          <div className="p-3 pt-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            {/* Header row */}
            <div className="grid grid-cols-3 gap-2 mt-3 mb-2 text-xs font-semibold" style={{ color: colors.textMuted }}>
              <div className="text-center">Line</div>
              <div className="text-center">Over</div>
              <div className="text-center">Under</div>
            </div>
            {/* Lines */}
            {sortedPoints.map((point) => {
              const group = lineGroups[point];
              const overInSlip = group.over && isInBetSlip({ key: category.marketKey }, group.over);
              const underInSlip = group.under && isInBetSlip({ key: category.marketKey }, group.under);

              return (
                <div key={point} className="grid grid-cols-3 gap-2 mb-2">
                  <div className="flex items-center justify-center text-sm font-bold" style={{ color: colors.textMuted }}>
                    {point >= 0 ? '+' : ''}{point}
                  </div>
                  {group.over ? (
                    <button
                      onClick={() => onAddToBetSlip(game, { key: category.marketKey }, group.over)}
                      className={`p-2 rounded transition text-center`}
                      style={overInSlip
                        ? { background: 'rgba(74,222,128,0.3)', border: '2px solid #4ade80' }
                        : { background: 'rgba(74,222,128,0.1)', border: '2px solid rgba(74,222,128,0.3)' }}
                    >
                      <div className="text-sm font-bold" style={{ color: overInSlip ? '#86efac' : '#4ade80' }}>
                        {formatOdds(group.over.price)}
                      </div>
                    </button>
                  ) : (
                    <div className="p-2 text-center" style={{ color: colors.textFaint }}>-</div>
                  )}
                  {group.under ? (
                    <button
                      onClick={() => onAddToBetSlip(game, { key: category.marketKey }, group.under)}
                      className={`p-2 rounded transition text-center`}
                      style={underInSlip
                        ? { background: 'rgba(239,68,68,0.3)', border: '2px solid #ef4444' }
                        : { background: 'rgba(239,68,68,0.1)', border: '2px solid rgba(239,68,68,0.3)' }}
                    >
                      <div className="text-sm font-bold" style={{ color: underInSlip ? '#fca5a5' : '#f87171' }}>
                        {formatOdds(group.under.price)}
                      </div>
                    </button>
                  ) : (
                    <div className="p-2 text-center" style={{ color: colors.textFaint }}>-</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderYesNoProp = (player, outcomes, category) => {
    const outcome = outcomes[0];
    const inSlip = isInBetSlip({ key: category.marketKey }, outcome);

    // Determine if this is a touchdown prop (football) or other type
    const isTouchdownProp = category.marketKey.includes('_td') || category.marketKey === 'player_tds_over';

    return (
      <button
        onClick={() => onAddToBetSlip(game, { key: category.marketKey }, outcome)}
        className={`w-full p-2 rounded-lg transition`}
        style={inSlip
          ? { background: colors.cardBgActive, border: `2px solid ${colors.gold}`, color: colors.textPrimary }
          : { background: 'rgba(0,0,0,0.25)', border: 'none', color: colors.textPrimary }}
      >
        <div className="text-xs font-semibold mb-1 truncate">{player}</div>
        {outcome.point && isTouchdownProp && (
          <div className="text-xs font-bold mb-1" style={{ color: inSlip ? colors.textPrimary : colors.textMuted }}>
            {outcome.point}+ TDs
          </div>
        )}
        <div className="text-sm font-bold" style={{ color: inSlip ? colors.textPrimary : '#BF953F' }}>
          {formatOdds(outcome.price)}
        </div>
      </button>
    );
  };

  const renderOverUnderProp = (player, outcomes, category) => {
    // Filter out invalid odds (e.g., -infinity for settled live bets)
    const validOutcomes = filterValidOutcomes(outcomes);
    if (validOutcomes.length === 0) return null;

    // Sort: Over first, Under second
    const sortedOutcomes = [...validOutcomes].sort((a, b) => {
      const aIsOver = a.description?.includes('Over');
      const bIsOver = b.description?.includes('Over');
      if (aIsOver && !bIsOver) return -1;
      if (!aIsOver && bIsOver) return 1;
      return 0;
    });

    const line = validOutcomes[0]?.point;
    
    return (
      <div className="rounded-lg overflow-hidden mb-3" style={{ background: 'rgba(0,0,0,0.25)', boxShadow: colors.shadowCard }}>
        <div className="px-2 py-1.5 flex items-center justify-between" style={{ background: 'rgba(0,0,0,0.2)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="font-semibold text-sm truncate flex-1" style={{ color: colors.textPrimary }}>{player}</div>
          {line && (
            <div className="text-sm font-semibold ml-2 flex-shrink-0" style={{ color: colors.textMuted }}>
              {category.categoryLabel} {line}
            </div>
          )}
        </div>
        <div className="grid grid-cols-2" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          {sortedOutcomes.map((outcome, idx) => {
            const inSlip = isInBetSlip({ key: category.marketKey }, outcome);
            const isOver = outcome.description?.includes('Over') || idx === 0;

            return (
              <button
                key={idx}
                onClick={() => onAddToBetSlip(game, { key: category.marketKey }, outcome)}
                className={`p-3 transition`}
                style={inSlip
                  ? { background: colors.cardBgActive, borderRight: idx === 0 ? '1px solid rgba(255,255,255,0.08)' : 'none' }
                  : { borderRight: idx === 0 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}
              >
                <div className="font-bold mb-2 text-xs tracking-wide" style={{ color: isOver ? (inSlip ? '#86efac' : '#4ade80') : (inSlip ? '#fca5a5' : '#f87171') }}>
                  {isOver ? 'Over' : 'Under'} {line}
                </div>
                <div className="text-sm font-bold" style={{ color: inSlip ? colors.textPrimary : '#BF953F' }}>
                  {formatOdds(outcome.price)}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const categoryGroups = getCategoryGroups();
  const selectedCategoryData = selectedCategory && loadedCategories[selectedCategory];
  const organizedProps = (() => {
    if (!selectedCategoryData) return {};
    const organized = organizePropsByCategory(selectedCategoryData);
    const categoryOrder = categoryGroups[selectedCategory] || [];
    return Object.fromEntries(
      Object.entries(organized)
        .filter(([, a]) => categoryOrder.includes(a.marketKey))
        .sort(([, a], [, b]) => categoryOrder.indexOf(a.marketKey) - categoryOrder.indexOf(b.marketKey))
    );
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-0 md:p-4">
      <div className="rounded-none md:rounded-lg w-full h-full md:max-w-7xl md:max-h-[85vh] flex flex-col" style={{ background: 'rgba(20,5,25,0.97)', boxShadow: colors.shadowRaised, color: colors.textPrimary, fontFamily: 'Nunito, sans-serif' }}>
        {/* Header */}
        <div className="p-3 md:p-4 flex justify-between items-center flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'linear-gradient(to right, rgba(99,41,101,0.4), rgba(63,29,91,0.4))' }}>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg md:text-xl font-bold" style={{ color: colors.textPrimary }}>Player Props</h2>
            <p className="text-xs md:text-sm truncate" style={{ color: colors.textMuted }}>
              <span className="font-semibold">{game.away_team}</span> @ <span className="font-semibold">{game.home_team}</span>
            </p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full transition ml-2" style={{ color: colors.textMuted }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile Category Tabs */}
        <div className="md:hidden overflow-x-auto scrollbar-hide flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}>
          <div className="flex p-2 gap-2 min-w-max">
            {Object.keys(categoryGroups).map(categoryName => (
              <button
                key={categoryName}
                onClick={() => loadCategoryProps(categoryName)}
                className={`px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition`}
                style={selectedCategory === categoryName
                  ? { background: '#BF953F', color: '#200e22' }
                  : { background: 'rgba(255,255,255,0.1)', color: colors.textPrimary, border: 'none' }}
              >
                {categoryName}
              </button>
            ))}
          </div>
        </div>

        {/* Content with Sidebar */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar - Hidden on mobile */}
          <div className="hidden md:block w-56 overflow-y-auto flex-shrink-0" style={{ background: 'rgba(0,0,0,0.2)', borderRight: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="p-3">
              <h3 className="text-xs font-semibold uppercase mb-2 px-2" style={{ color: colors.textMuted }}>Categories</h3>
              {Object.keys(categoryGroups).map(categoryName => (
                <button
                  key={categoryName}
                  onClick={() => loadCategoryProps(categoryName)}
                  className={`w-full text-left px-3 py-2 rounded mb-1 text-sm transition`}
                  style={selectedCategory === categoryName
                    ? { background: '#BF953F', color: '#200e22', fontWeight: 600 }
                    : { color: colors.textMuted }}
                >
                  {categoryName}
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-2 md:p-4">
            {!selectedCategory ? (
              <div className="text-center py-8 md:py-12" style={{ color: colors.textMuted }}>
                <p className="text-base md:text-lg mb-2">Select a category to view props</p>
                <p className="text-xs md:text-sm">Choose from {window.innerWidth < 768 ? 'above' : 'the sidebar'} to load betting options</p>
              </div>
            ) : loadingCategory ? (
              <div className="text-center py-8 md:py-12">
                <div className="animate-spin rounded-full h-8 w-8 md:h-12 md:w-12 border-4 border-t-transparent mx-auto mb-4" style={{ borderColor: '#BF953F', borderTopColor: 'transparent' }}></div>
                <p className="text-sm" style={{ color: colors.textMuted }}>Loading {selectedCategory}...</p>
              </div>
            ) : Object.keys(organizedProps).length === 0 ? (
              <div className="text-center py-8 md:py-12" style={{ color: colors.textMuted }}>
                <p className="text-base md:text-lg mb-2">No props available for {selectedCategory}</p>
                <p className="text-xs md:text-sm">Player props are typically released 12-24 hours before game time.</p>
              </div>
            ) : (
              <div className="space-y-4 md:space-y-6">
                {Object.entries(organizedProps).map(([propName, category]) => {
                  const playersList = Object.entries(category.players);
                  const isAltCategoryExpanded = expandedAltCategories[propName];
                  const hasSelectionsInSlip = playersList.some(([player, outcomes]) =>
                    outcomes.some(outcome => isInBetSlip({ key: category.marketKey }, outcome))
                  );

                  return (
                    <div key={propName} className="rounded-lg overflow-hidden" style={{ background: 'rgba(0,0,0,0.25)', boxShadow: colors.shadowCard }}>
                      {category.isAlternate ? (
                        // Alternate props - entire category is collapsible
                        <>
                          <button
                            onClick={() => toggleAltCategoryExpand(propName)}
                            className={`w-full p-3 md:p-4 flex items-center justify-between transition`}
                            style={hasSelectionsInSlip ? { background: colors.goldActiveBg } : {}}
                          >
                            <div className="flex items-center gap-2 md:gap-3 min-w-0">
                              <h3 className="text-sm md:text-lg font-bold truncate" style={{ color: colors.textPrimary }}>{propName}</h3>
                              {hasSelectionsInSlip && (
                                <span className="text-[10px] md:text-xs px-2 py-0.5 rounded flex-shrink-0" style={{ background: '#BF953F', color: '#200e22' }}>In Slip</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs md:text-sm" style={{ color: colors.textMuted }}>
                                {playersList.length}
                              </span>
                              {isAltCategoryExpanded ? (
                                <ChevronDown className="w-4 h-4 md:w-5 md:h-5" style={{ color: colors.textMuted }} />
                              ) : (
                                <ChevronRight className="w-4 h-4 md:w-5 md:h-5" style={{ color: colors.textMuted }} />
                              )}
                            </div>
                          </button>
                          {isAltCategoryExpanded && (
                            <div className="p-2 md:p-4 pt-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                              {playersList.map(([player, outcomes]) => (
                                <div key={player}>
                                  {renderAlternateProp(player, outcomes, category, propName)}
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="p-2 md:p-4">
                          <h3 className="text-sm md:text-lg font-bold mb-3 md:mb-4" style={{ color: colors.textPrimary }}>{propName}</h3>
                          {category.isYesNoProp ? (
                            // Yes/No props - responsive grid
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 md:gap-3">
                              {playersList.map(([player, outcomes]) => (
                                <div key={player}>{renderYesNoProp(player, outcomes, category)}</div>
                              ))}
                            </div>
                          ) : (
                            // Regular over/under - responsive grid
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-3">
                              {playersList.map(([player, outcomes]) => (
                                <div key={player}>{renderOverUnderProp(player, outcomes, category)}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-3 md:p-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs md:text-sm" style={{ color: colors.textMuted }}>
                <span className="font-semibold" style={{ color: colors.textPrimary }}>{betSlip.length}</span> bet{betSlip.length !== 1 ? 's' : ''} in slip
              </p>
            </div>
            <button
              onClick={onClose}
              className="px-6 md:px-8 py-2 md:py-2.5 rounded-lg transition font-semibold shadow-md text-sm"
              style={{ background: '#BF953F', color: '#200e22' }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Support Hub (Rules / FAQ / Messages) ────────────────────────────────────

function SupportView(props) {
  const [section, setSection] = React.useState('messages');
  const [rules, setRules] = React.useState([]);
  const [faqs, setFaqs] = React.useState([]);
  const [rulesLoading, setRulesLoading] = React.useState(false);

  React.useEffect(() => {
    if (section === 'rules' || section === 'faq') loadRules();
    if (section === 'messages') props.onLoadMessages();
  }, [section]);

  const loadRules = async () => {
    if (rules.length > 0 || faqs.length > 0) return;
    setRulesLoading(true);
    try {
      const data = await (await import('../api')).api.getRules();
      setRules(data.rules || []);
      setFaqs(data.faqs || []);
    } catch {}
    finally { setRulesLoading(false); }
  };

  const gold = colors.textPrimary;
  const muted = colors.textMuted;
  const accent = '#BF953F';

  const tabs = [
    { id: 'messages', label: '✉ Messages' },
    { id: 'rules', label: '📋 Rules' },
    { id: 'faq', label: '❓ FAQ' },
  ];

  return (
    <div>
      {/* Sub-navigation */}
      <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', background: 'rgba(0,0,0,0.25)', borderRadius: 8, padding: '0.35rem' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setSection(t.id)}
            style={{
              flex: 1,
              padding: '0.55rem 0.5rem',
              borderRadius: 6,
              border: 'none',
              cursor: 'pointer',
              fontFamily: '"Nunito", sans-serif',
              fontWeight: section === t.id ? 700 : 400,
              fontSize: '0.88rem',
              color: section === t.id ? gold : muted,
              background: section === t.id ? colors.goldActiveBg : 'transparent',
              transition: 'all 0.15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Messages section */}
      {section === 'messages' && (
        <MessagesView
          messages={props.messages}
          selectedMessage={props.selectedMessage}
          messageView={props.messageView}
          onViewChange={props.onViewChange}
          onOpenMessage={props.onOpenMessage}
          onSendNew={props.onSendNew}
          onReply={props.onReply}
          onLoadMessages={props.onLoadMessages}
          onBack={props.onBack}
        />
      )}

      {/* Rules section */}
      {section === 'rules' && (
        <div className="rounded-lg p-6" style={{ background: colors.cardBg, boxShadow: colors.shadowCard, color: gold }}>
          <h2 className="text-xl font-bold mb-4" style={{ color: gold }}>Rules</h2>
          {rulesLoading && <p style={{ color: muted }}>Loading…</p>}
          {!rulesLoading && rules.length === 0 && (
            <p style={{ color: muted, fontSize: '0.9rem' }}>No rules have been published yet.</p>
          )}
          {!rulesLoading && rules.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {rules.map((r, i) => (
                <div key={r.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <span style={{ color: accent, fontWeight: 700, minWidth: 24, flexShrink: 0 }}>{i + 1}.</span>
                  <span style={{ color: gold, fontSize: '0.95rem', lineHeight: 1.6 }}>{r.content}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAQ section */}
      {section === 'faq' && (
        <div className="rounded-lg p-6" style={{ background: colors.cardBg, boxShadow: colors.shadowCard, color: gold }}>
          <h2 className="text-xl font-bold mb-4" style={{ color: gold }}>Frequently Asked Questions</h2>
          {rulesLoading && <p style={{ color: muted }}>Loading…</p>}
          {!rulesLoading && faqs.length === 0 && (
            <p style={{ color: muted, fontSize: '0.9rem' }}>No FAQ entries have been published yet.</p>
          )}
          {!rulesLoading && faqs.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {faqs.map((f, i) => (
                <div key={f.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <span style={{ color: accent, fontWeight: 700, minWidth: 24, flexShrink: 0 }}>{i + 1}.</span>
                  <span style={{ color: gold, fontSize: '0.95rem', lineHeight: 1.6 }}>{f.content}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Messages (used by SupportView above) ────────────────────────────────────

function MessagesView({ messages, selectedMessage, messageView, onViewChange, onOpenMessage, onSendNew, onReply, onLoadMessages, onBack }) {
  const [replyText, setReplyText] = React.useState('');

  React.useEffect(() => {
    onLoadMessages();
  }, [messageView]);

  if (selectedMessage) {
    return (
      <div className="rounded-lg p-6" style={{ background: colors.cardBg, boxShadow: colors.shadowCard, color: colors.textPrimary }}>
        <button
          onClick={onBack}
          className="mb-4 flex items-center gap-2"
          style={{ color: '#BF953F' }}
        >
          ← Back to {messageView === 'inbox' ? 'Inbox' : 'Sent'}
        </button>

        <div className="pb-4 mb-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>{selectedMessage.message.subject}</h2>
            {selectedMessage.message.is_resolved && (
              <span className="px-3 py-1 rounded text-sm" style={{ background: 'rgba(74,222,128,0.2)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)' }}>
                Resolved
              </span>
            )}
          </div>
          <div className="text-sm" style={{ color: colors.textMuted }}>
            <p>From: {selectedMessage.message.sender_name}</p>
            <p>To: {selectedMessage.message.recipient_name}</p>
            <p>Date: {new Date(selectedMessage.message.created_at).toLocaleString()}</p>
          </div>
        </div>

        <div className="rounded p-4 mb-4" style={{ background: 'rgba(0,0,0,0.2)', boxShadow: colors.shadowSubtle }}>
          <p className="whitespace-pre-wrap" style={{ color: colors.textPrimary }}>{selectedMessage.message.message}</p>
        </div>

        {selectedMessage.replies && selectedMessage.replies.length > 0 && (
          <div className="space-y-4 mb-4">
            <h3 className="font-semibold" style={{ color: colors.textPrimary }}>Replies:</h3>
            {selectedMessage.replies.map((reply) => (
              <div key={reply.id} className="rounded p-4 ml-8" style={{ background: colors.sectionBg, boxShadow: colors.shadowSubtle }}>
                <div className="text-sm mb-2" style={{ color: colors.textMuted }}>
                  <strong style={{ color: colors.textPrimary }}>{reply.sender_name}</strong> - {new Date(reply.created_at).toLocaleString()}
                </div>
                <p className="whitespace-pre-wrap" style={{ color: colors.textPrimary }}>{reply.message}</p>
              </div>
            ))}
          </div>
        )}

        {messageView === 'inbox' && (
          <div className="pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <h3 className="font-semibold mb-2" style={{ color: colors.textPrimary }}>Reply:</h3>
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              className="w-full rounded p-3 mb-2 focus:outline-none"
              style={{ background: 'rgba(0,0,0,0.3)', boxShadow: colors.shadowCard, color: colors.textPrimary }}
              rows="4"
            />
            <button
              onClick={() => {
                if (replyText.trim()) {
                  onReply(selectedMessage.message.id, replyText);
                  setReplyText('');
                }
              }}
              className="px-4 py-2 rounded"
              style={{ background: '#BF953F', color: '#200e22' }}
            >
              Send Reply
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-lg" style={{ background: colors.cardBg, boxShadow: colors.shadowCard, color: colors.textPrimary }}>
      <div className="p-4 flex justify-between items-center" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex gap-2">
          <button
            onClick={() => onViewChange('inbox')}
            className="px-4 py-2 rounded"
            style={messageView === 'inbox'
              ? { background: '#BF953F', color: '#200e22' }
              : { background: 'rgba(255,255,255,0.1)', color: colors.textPrimary }}
          >
            Inbox
          </button>
          <button
            onClick={() => onViewChange('sent')}
            className="px-4 py-2 rounded"
            style={messageView === 'sent'
              ? { background: '#BF953F', color: '#200e22' }
              : { background: 'rgba(255,255,255,0.1)', color: colors.textPrimary }}
          >
            Sent
          </button>
        </div>
        <button
          onClick={onSendNew}
          className="px-4 py-2 rounded flex items-center gap-2"
          style={{ background: 'rgba(74,222,128,0.2)', color: '#4ade80', border: '1px solid rgba(74,222,128,0.3)' }}
        >
          ✉️ New Message
        </button>
      </div>

      <div>
        {messages.length === 0 ? (
          <div className="p-8 text-center" style={{ color: colors.textMuted }}>
            No messages yet
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              onClick={() => onOpenMessage(message)}
              className="p-4 cursor-pointer"
              style={{
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                background: !message.is_read && messageView === 'inbox' ? colors.goldActiveBg : 'transparent'
              }}
            >
              <div className="flex justify-between items-start mb-1">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    {!message.is_read && messageView === 'inbox' && (
                      <span className="w-2 h-2 rounded-full" style={{ background: '#BF953F' }}></span>
                    )}
                    <h3 className="font-semibold" style={{ color: colors.textPrimary }}>{message.subject}</h3>
                    {message.is_resolved && (
                      <span className="text-xs px-2 py-1 rounded" style={{ background: 'rgba(74,222,128,0.2)', color: '#4ade80' }}>
                        Resolved
                      </span>
                    )}
                  </div>
                  <p className="text-sm" style={{ color: colors.textMuted }}>
                    {messageView === 'inbox'
                      ? `From: ${message.sender_name}`
                      : `To: ${message.recipient_name}`}
                  </p>
                </div>
                <div className="text-xs" style={{ color: colors.textFaint }}>
                  {new Date(message.created_at).toLocaleDateString()}
                </div>
              </div>
              <p className="text-sm line-clamp-2" style={{ color: colors.textMuted }}>{message.message}</p>
              {message.reply_count > 0 && (
                <p className="text-xs mt-1" style={{ color: '#BF953F' }}>
                  {message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}
                </p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function MultiBetConfirmModal({ bets, amount, useFreePlay, onConfirm, onCancel }) {
  const total = amount * bets.length;

  const betLabel = (bet) => {
    if (bet.type === 'futures') return bet.description;
    return `${bet.away_team} @ ${bet.home_team} — ${bet.description}`;
  };

  const formatOdds = (odds) => {
    if (odds === null || odds === undefined || odds === '') return '';
    const s = String(odds).trim();
    if (s.startsWith('+') || s.startsWith('-')) return s; // already formatted — don't add a second '+'
    const n = Number(s);
    if (Number.isNaN(n)) return s;
    return n > 0 ? `+${n}` : `${n}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="rounded-lg w-full max-w-lg p-6" style={{ background: 'rgba(20,5,25,0.97)', boxShadow: colors.shadowRaised, color: colors.textPrimary, fontFamily: 'Nunito, sans-serif' }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold" style={{ color: colors.textPrimary }}>Confirm {bets.length} Single Bets</h2>
          <button onClick={onCancel} className="text-2xl leading-none" style={{ color: colors.textMuted }}>×</button>
        </div>

        <div className="scrollbar-hide mb-4 space-y-2" style={{ maxHeight: '260px', overflowY: 'auto' }}>
          {bets.map((bet, i) => (
            <div key={i} className="flex justify-between items-start rounded px-3 py-2 text-sm" style={{ background: 'rgba(0,0,0,0.3)', boxShadow: colors.shadowSubtle }}>
              <span style={{ color: colors.textMuted, flex: 1, marginRight: '12px' }}>{betLabel(bet)}</span>
              <span className="font-bold whitespace-nowrap" style={{ color: colors.odds }}>{formatOdds(bet.odds)}</span>
            </div>
          ))}
        </div>

        <div className="rounded px-4 py-3 mb-5 text-sm" style={{ background: colors.sectionBg, boxShadow: colors.shadowSubtle }}>
          <div className="flex justify-between mb-1">
            <span style={{ color: colors.textMuted }}>Amount per bet</span>
            <span>${amount.toFixed(2)} ₵₵</span>
          </div>
          <div className="flex justify-between mb-1">
            <span style={{ color: colors.textMuted }}>Number of bets</span>
            <span>{bets.length}</span>
          </div>
          <div className="flex justify-between font-bold text-base pt-1" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '4px' }}>
            <span>Total cost</span>
            <span>${total.toFixed(2)} ₵₵{useFreePlay ? ' (Free Play)' : ''}</span>
          </div>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onConfirm}
            className="flex-1 py-2 rounded font-bold"
            style={{ background: '#BF953F', color: '#200e22' }}
          >
            Place {bets.length} Bets
          </button>
          <button
            onClick={onCancel}
            className="px-5 py-2 rounded"
            style={{ background: 'rgba(0,0,0,0.3)', boxShadow: colors.shadowCard, color: colors.textMuted }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function NewMessageModal({ onClose, onSend }) {
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState('');

  const handleSend = () => {
    if (!subject.trim() || !message.trim()) {
      setError('Please fill in both subject and message');
      return;
    }
    setError('');
    onSend(subject, message);
    setSubject('');
    setMessage('');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="rounded-lg max-w-2xl w-full p-6" style={{ background: 'rgba(20,5,25,0.97)', boxShadow: colors.shadowRaised, color: colors.textPrimary, fontFamily: 'Nunito, sans-serif' }}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold" style={{ color: colors.textPrimary }}>Contact Customer Support</h2>
          <button onClick={onClose} className="text-2xl" style={{ color: colors.textMuted }}>
            ×
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="What do you need help with?"
              className="w-full rounded px-3 py-2 focus:outline-none"
              style={{ background: 'rgba(0,0,0,0.3)', boxShadow: colors.shadowCard, color: colors.textPrimary }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: colors.textMuted }}>Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe your issue or question..."
              className="w-full rounded px-3 py-2 focus:outline-none"
              style={{ background: 'rgba(0,0,0,0.3)', boxShadow: colors.shadowCard, color: colors.textPrimary }}
              rows="6"
            />
          </div>

          {error && <p className="text-sm" style={{ color: colors.negative }}>{error}</p>}

          <div className="flex gap-2">
            <button
              onClick={handleSend}
              className="flex-1 py-2 rounded"
              style={{ background: '#BF953F', color: '#200e22', fontWeight: 600 }}
            >
              Send Message
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded"
              style={{ background: 'rgba(255,255,255,0.1)', color: colors.textPrimary, boxShadow: colors.shadowCard }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function GamePropsModal({ game, props, loading, title = 'Game Markets', onClose, onAddToBetSlip, betSlip }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const tabScroll = useDragScroll();
  const [collapsedCategories, setCollapsedCategories] = useState({});
  // Default: collapsed. Only expand when user explicitly opens (value === false).
  const toggleCategory = (label) => setCollapsedCategories(prev => ({
    ...prev,
    [label]: prev[label] === false ? true : false,
  }));

  const isInBetSlip = (market, outcome) => {
    const key = getBetKey(game.id, market.key, outcome);
    return betSlip.some(bet => bet.bet_key === key);
  };

  // Check if odds are valid (not infinity, not too extreme)
  const isValidOdds = (price) => {
    if (typeof price !== 'number') return false;
    if (!isFinite(price)) return false;
    if (price < 1.01) return false;
    if (price > 10000) return false;
    return true;
  };

  const formatOdds = (price) => {
    if (typeof price === 'string') return price;
    if (typeof price === 'number') {
      if (!isValidOdds(price)) return null;
      if (price >= 2.0) {
        return `+${Math.round((price - 1) * 100)}`;
      } else {
        return `${Math.round(-100 / (price - 1))}`;
      }
    }
    return price;
  };

  // Filter outcomes to only include those with valid odds
  const filterValidOutcomes = (outcomes) => {
    if (!outcomes) return [];
    return outcomes.filter(o => isValidOdds(o.price));
  };

  const organizeGamePropsByCategory = (propsData) => {
    const empty = { lines: [], halves: [], quarters: [], periods: [], innings: [], sets: [], rounds: [], outrights: [], teamTotals: [], oddEven: [], players: [], specials: [], maps: [] };
    if (!propsData?.bookmakers?.[0]?.markets) return empty;

    const organized = { ...empty };

    propsData.bookmakers[0].markets.forEach(market => {
      const k = market.key;

      // Player props (basketball, NFL, baseball, hockey, soccer goal scorers)
      if (k.startsWith('player_') || k.startsWith('batter_') || k.startsWith('pitcher_') ||
          k.includes('goal_scorer') || k.includes('anytime_scorer')) {
        organized.players.push(market);
      // Outrights (golf, tournament winner, round leaders)
      } else if (k.startsWith('outright_') || k.match(/^round_\d_leader$/) || k.startsWith('top_') || k === 'make_cut') {
        organized.outrights.push(market);
      // Rounds (MMA/Boxing fight markets)
      } else if (k === 'method_of_victory' || k === 'round_betting' || k === 'goes_distance' || k === 'totals_rounds' || k === 'round_group') {
        organized.rounds.push(market);
      // Sets (Tennis)
      } else if (/_s[1-5]$/.test(k) || k === 'totals_sets' || k === 'totals_games' || k === 'set_score' || k === 'spreads_games') {
        organized.sets.push(market);
      // Odd/Even and Double Chance
      } else if (k.startsWith('odd_even') || k.startsWith('double_chance')) {
        organized.oddEven.push(market);
      // BTTS and soccer specials
      } else if (k.startsWith('btts') || k.startsWith('corners_') || k === 'first_goal_team' || k === 'last_goal_team' || k === 'next_goal' || k.startsWith('clean_sheet') || k === 'result_btts' || k === 'dc_btts' || k.startsWith('win_by_') || k === 'clean_sheet_either' || k === 'regular_time' || k === 'overtime_result' || k === 'exact_goals' || k.startsWith('team_to_score') || k.startsWith('correct_score_')) {
        organized.specials.push(market);
      // Team totals
      } else if (k.startsWith('team_total')) {
        organized.teamTotals.push(market);
      // Per-map moneyline (esports)
      } else if (/_map\d+$/.test(k)) {
        organized.maps.push(market);
      // NHL Periods (_p1, _p2, _p3)
      } else if (/_p[1-3]$/.test(k)) {
        organized.periods.push(market);
      // Basketball / NFL quarters (_q1–_q4)
      } else if (/_q[1-4]$/.test(k)) {
        organized.quarters.push(market);
      // Baseball first 5 innings or inning-specific
      } else if (/_f5$/.test(k) || /_i[1-9]$/.test(k)) {
        organized.innings.push(market);
      // Halves (_h1 / _h2)
      } else if (/_h[12]$/.test(k)) {
        organized.halves.push(market);
      // Specials / combos
      } else if (k.startsWith('race_to') || k.startsWith('highest_scoring') ||
                 k.startsWith('team_highest') || k.startsWith('ht_ft') ||
                 k.startsWith('result_total') || k === 'overtime') {
        organized.specials.push(market);
      } else {
        organized.lines.push(market);
      }
    });

    return organized;
  };

  const renderMarket = (market) => {
    const rawLabel = GAME_MARKET_LABELS[market.key] || market.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    // Replace generic "Home"/"Away" with actual team names
    const home = game.home_team || 'Home';
    const away = game.away_team || 'Away';
    const displayName = rawLabel.replace(/\bHome\b/g, home).replace(/\bAway\b/g, away);

    const validOutcomes = filterValidOutcomes(market.outcomes);
    if (validOutcomes.length === 0) return null;

    const hasPoints = validOutcomes.some(o => o.point !== undefined);
    const isOverUnder = hasPoints && validOutcomes.some(o => /over|under/i.test(o.name));

    // Uniform grid layout for all outcome types (moneyline, spread, over/under)
    // Spreads show team name + spread point stacked; O/U show +line/-line in green/red
    return (
      <div key={market.key} className="rounded-lg overflow-hidden h-full flex flex-col" style={{ background: 'rgba(0,0,0,0.25)', boxShadow: colors.shadowCard }}>
        {/* Market header */}
        <div className="px-3 py-2 flex-shrink-0" style={{ background: colors.sectionBg, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <span className="text-xs font-bold tracking-wide" style={{ color: colors.textPrimary }}>{displayName}</span>
        </div>
        {/* Outcomes — always a uniform grid, stretched to fill any extra height from taller siblings */}
        <div className="grid flex-1" style={{
          gridTemplateColumns: `repeat(${Math.min(validOutcomes.length, 3)}, 1fr)`,
          gap: '1px',
          background: 'rgba(255,255,255,0.08)',
        }}>
          {validOutcomes.map((outcome, idx) => {
            const inSlip = isInBetSlip(market, outcome);
            const isOver = isOverUnder && /over/i.test(outcome.name);
            return (
              <button
                key={idx}
                onClick={() => onAddToBetSlip(game, market, outcome)}
                className="h-full flex flex-col items-center justify-center py-3.5 px-2 text-center transition active:scale-95"
                style={{ background: inSlip ? colors.cardBgActive : 'rgba(20,5,25,0.97)' }}
              >
                {/* Top line: team name (spread/ML), +line/-line (O/U) */}
                <div className="text-xs font-semibold truncate mb-1" style={{ color: colors.textPrimary }}>
                  {isOverUnder
                    ? (isOver ? `O ${outcome.point}` : `U ${outcome.point}`)
                    : (outcome.name || '').replace(/\bHome\b/g, home).replace(/\bAway\b/g, away)
                  }
                </div>
                {/* Bottom line: spread point (spreads only), then odds */}
                {hasPoints && !isOverUnder && outcome.point !== undefined && (
                  <div className="text-xs mb-0.5" style={{ color: colors.textPrimary }}>
                    {outcome.point > 0 ? '+' : ''}{outcome.point}
                  </div>
                )}
                <div className="text-sm font-bold" style={{ color: inSlip ? colors.textPrimary : colors.odds }}>
                  {formatOdds(outcome.price)}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Parse an odds-api.io player prop outcome.
  // Format: name=player name, description='Over'|'Under', point=line value.
  // Returns { playerName, direction: 'over'|'under'|null, line: number|null }
  const parsePlayerOutcome = (outcome) => {
    const n = outcome.name || '';
    const desc = outcome.description || '';
    const dir = /over/i.test(desc) ? 'over' : /under/i.test(desc) ? 'under' : null;
    const line = outcome.point !== undefined ? outcome.point : null;
    return { playerName: n, direction: dir, line };
  };

  // Player props tab — collapsible categories, player name header + big +/- buttons
  const renderPlayerPropsTab = (markets) => {
    const isBaseball = game.sport_key?.includes('baseball');
    // Basketball-only market keys that make no sense in baseball context
    const BASKETBALL_ONLY_KEYS = new Set([
      'player_points','player_points_alternate','player_points_q1',
      'player_rebounds','player_rebounds_alternate','player_rebounds_q1',
      'player_assists','player_assists_alternate','player_assists_q1',
      'player_blocks','player_blocks_alternate','player_steals','player_steals_alternate',
      'player_blocks_steals','player_turnovers','player_turnovers_alternate',
      'player_threes','player_threes_alternate','player_field_goals',
      'player_frees_made','player_frees_attempts','player_shots',
      'player_points_rebounds_assists','player_points_rebounds_assists_alternate',
      'player_points_rebounds','player_points_rebounds_alternate',
      'player_points_assists','player_points_assists_alternate',
      'player_rebounds_assists','player_rebounds_assists_alternate',
      'player_double_double','player_triple_double','player_first_basket',
      'player_first_team_basket','player_method_of_first_basket','player_passes',
    ]);

    // Desired display order for player prop market keys, sport-specific
    const sportKey = game.sport_key || '';
    const PROP_ORDER = sportKey.startsWith('basketball_') ? [
      'player_points', 'player_points_alternate', 'player_points_q1',
      'player_rebounds', 'player_rebounds_alternate', 'player_rebounds_q1',
      'player_assists', 'player_assists_alternate', 'player_assists_q1',
      'player_points_rebounds_assists', 'player_points_rebounds_assists_alternate',
      'player_points_rebounds', 'player_points_rebounds_alternate',
      'player_points_assists', 'player_points_assists_alternate',
      'player_rebounds_assists', 'player_rebounds_assists_alternate',
      'player_threes', 'player_threes_alternate',
      'player_blocks', 'player_blocks_alternate',
      'player_steals', 'player_steals_alternate',
      'player_blocks_steals', 'player_turnovers', 'player_turnovers_alternate',
      'player_double_double', 'player_triple_double',
      'player_first_basket', 'player_first_team_basket', 'player_method_of_first_basket',
    ] : sportKey === 'icehockey_nhl' ? [
      'player_points', 'player_goals', 'player_assists',
      'player_shots_on_goal', 'player_blocked_shots',
      'player_saves',
      'player_power_play_points',
      'player_goal_scorer_anytime', 'player_goal_scorer_first', 'player_goal_scorer_last',
    ] : sportKey === 'baseball_mlb' ? [
      'pitcher_strikeouts', 'pitcher_outs', 'pitcher_hits_allowed', 'pitcher_earned_runs', 'pitcher_walks', 'pitcher_record_a_win',
      'batter_hits', 'batter_total_bases', 'batter_runs_scored',
      'batter_home_runs', 'batter_rbis', 'batter_first_home_run',
      'batter_singles', 'batter_doubles', 'batter_triples', 'batter_stolen_bases',
      'batter_walks', 'batter_strikeouts', 'batter_hits_runs_rbis',
    ] : [
      // NFL default
      'player_anytime_td', 'player_1st_td', 'player_last_td', 'player_tds_over',
      'player_pass_yds', 'player_pass_tds', 'player_pass_completions', 'player_pass_attempts', 'player_pass_interceptions',
      'player_rush_yds', 'player_rush_tds', 'player_rush_attempts',
      'player_receptions', 'player_reception_yds', 'player_reception_tds',
      'player_pass_rush_yds', 'player_rush_reception_yds', 'player_rush_reception_tds',
      'player_sacks', 'player_tackles_assists', 'player_solo_tackles', 'player_defensive_interceptions',
      'player_field_goals', 'player_kicking_points', 'player_pats',
    ];

    // Group markets by display label (e.g. "Player Points", "3-Pointers")
    const groups = {};
    markets.forEach(m => {
      // Skip basketball-only markets when viewing baseball
      if (isBaseball && BASKETBALL_ONLY_KEYS.has(m.key)) return;
      const label = GAME_MARKET_LABELS[m.key] || m.key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      if (!groups[label]) groups[label] = { key: m.key, markets: [] };
      groups[label].markets.push(m);
    });

    const sortedGroups = Object.entries(groups).sort(([, a], [, b]) => {
      const ai = PROP_ORDER.indexOf(a.key);
      const bi = PROP_ORDER.indexOf(b.key);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    return (
      <div className="space-y-2">
        {sortedGroups.map(([label, group]) => {
          const isCollapsed = collapsedCategories[label] !== false;

          // Parse all outcomes into player cards
          // Structure: { playerName → { over: {outcome, market}, under: {outcome, market}, line } }
          const playerMap = {};   // keyed by playerName or index for unnamed
          const outrightList = []; // for outcomes that have no line (goal scorers etc.)
          let itemCount = 0;

          const isAltMarket = group.key.endsWith('_alternate');

          group.markets.forEach(m => {
            const validOutcomes = filterValidOutcomes(m.outcomes);
            validOutcomes.forEach(o => {
              const { playerName, direction, line } = parsePlayerOutcome(o);
              const DIRECTION_WORDS = ['over','under','yes','no'];
              const isRealName = playerName
                && !DIRECTION_WORDS.includes(playerName.toLowerCase())
                && !(playerName && !isNaN(parseFloat(playerName)));

              if (direction && line !== null && isRealName) {
                if (isAltMarket) {
                  // Alternate lines: group by player → by line so we keep ALL lines
                  if (!playerMap[playerName]) playerMap[playerName] = { playerName, lines: {}, market: m };
                  if (!playerMap[playerName].lines[line]) playerMap[playerName].lines[line] = { over: null, under: null };
                  playerMap[playerName].lines[line][direction] = o;
                } else {
                  // Standard: one O/U per player (first line seen wins)
                  if (!playerMap[playerName]) playerMap[playerName] = { playerName, line, over: null, under: null, market: m };
                  if (direction === 'over' && !playerMap[playerName].over) playerMap[playerName].over = o;
                  if (direction === 'under' && !playerMap[playerName].under) playerMap[playerName].under = o;
                }
              } else if (isRealName) {
                // Outright: no line (first basket, anytime scorer) OR direction-less
                outrightList.push({ outcome: o, market: m });
              }
            });
          });

          const playerEntries = Object.values(playerMap);
          itemCount = playerEntries.length + outrightList.length;

          return (
            <div key={label} className="rounded-xl overflow-hidden" style={{ boxShadow: colors.shadowCard }}>
              {/* Collapsible category header */}
              <button
                onClick={() => toggleCategory(label)}
                className="w-full px-4 py-3.5 flex items-center justify-between transition"
                style={{ background: isCollapsed ? colors.sectionBg : colors.goldActiveBg }}
              >
                <span className="font-bold tracking-wider text-sm" style={{ color: colors.textPrimary }}>{label}</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded" style={{ background: colors.goldActiveBg, color: colors.textPrimary }}>{itemCount}</span>
                  <ChevronDown
                    className="w-4 h-4 transition-transform"
                    style={{ color: colors.textPrimary, transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
                  />
                </div>
              </button>

              {/* Content — hidden when collapsed */}
              {!isCollapsed && (
                <div className="p-3 space-y-2" style={{ background: 'rgba(0,0,0,0.2)' }}>
                  {isAltMarket ? (
                    /* Alternate lines: one full-width card per player showing all lines */
                    <div className="space-y-2">
                      {playerEntries.map((entry, i) => {
                        const { playerName, lines, market } = entry;
                        const sortedLines = Object.keys(lines).map(Number).sort((a, b) => a - b);
                        return (
                          <div key={i} className="rounded-xl overflow-hidden" style={{ background: 'rgba(0,0,0,0.3)', boxShadow: colors.shadowSubtle }}>
                            <div className="px-3 py-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                              <span className="font-bold text-sm" style={{ color: colors.textPrimary }}>{playerName}</span>
                            </div>
                            <table className="w-full text-xs">
                              <thead>
                                <tr style={{ background: colors.sectionBg }}>
                                  <th className="px-3 py-1.5 text-left font-semibold" style={{ color: colors.textMuted }}>Line</th>
                                  <th className="px-3 py-1.5 text-center font-semibold" style={{ color: colors.textPrimary }}>Over</th>
                                  <th className="px-3 py-1.5 text-center font-semibold" style={{ color: colors.textPrimary }}>Under</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortedLines.map((lineVal, li) => {
                                  const { over, under } = lines[lineVal];
                                  const overInSlip = over && isInBetSlip(market, over);
                                  const underInSlip = under && isInBetSlip(market, under);
                                  return (
                                    <tr key={li} style={{ borderTop: li > 0 ? '1px solid rgba(255,255,255,0.08)' : undefined }}>
                                      <td className="px-3 py-2 font-bold" style={{ color: colors.textPrimary }}>{lineVal}</td>
                                      <td className="px-2 py-1.5 text-center">
                                        {over ? (
                                          <button
                                            onClick={() => onAddToBetSlip(game, market, over)}
                                            className="px-3 py-1.5 rounded-lg font-bold transition active:scale-95 w-full"
                                            style={{ background: overInSlip ? colors.cardBgActive : colors.sectionBg, color: colors.odds, border: overInSlip ? `1px solid ${colors.gold}` : 'none' }}
                                          >
                                            {formatOdds(over.price)}
                                          </button>
                                        ) : <span style={{ color: colors.textFaint }}>—</span>}
                                      </td>
                                      <td className="px-2 py-1.5 text-center">
                                        {under ? (
                                          <button
                                            onClick={() => onAddToBetSlip(game, market, under)}
                                            className="px-3 py-1.5 rounded-lg font-bold transition active:scale-95 w-full"
                                            style={{ background: underInSlip ? colors.cardBgActive : colors.sectionBg, color: colors.odds, border: underInSlip ? `1px solid ${colors.gold}` : 'none' }}
                                          >
                                            {formatOdds(under.price)}
                                          </button>
                                        ) : <span style={{ color: colors.textFaint }}>—</span>}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    /* Standard O/U props — 3-column grid */
                    <div className="grid grid-cols-3 gap-2">
                      {playerEntries.map((entry, i) => {
                        const { playerName, line, over, under, market } = entry;
                        const overInSlip = over && isInBetSlip(market, over);
                        const underInSlip = under && isInBetSlip(market, under);
                        // Binary "will it happen" props (Anytime TD, Double-Double,
                        // ...) still arrive with a nominal 0.5 line from the book,
                        // but there's no real threshold to show — Yes/No reads
                        // right where "+0.5"/"-0.5" would look like a stat line.
                        // Every other O/U prop (points, yards, ...) gets O/U
                        // instead of a spread-style +/- sign, which only spreads
                        // themselves should use.
                        const isYesNo = YES_NO_MARKET_KEYS.has(market.key);
                        const overLabel = isYesNo ? 'Yes' : `O ${line}`;
                        const underLabel = isYesNo ? 'No' : `U ${line}`;
                        return (
                          <div key={i} className="rounded-xl overflow-hidden h-full flex flex-col" style={{ background: 'rgba(0,0,0,0.3)', boxShadow: colors.shadowSubtle }}>
                            {/* Player name — centered */}
                            <div className="px-1 py-2 text-center flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                              <span className="font-bold text-xs leading-tight" style={{ color: colors.textPrimary }}>
                                {playerName}
                              </span>
                            </div>
                            {/* Over / Under buttons — stretched to fill any extra height from taller sibling cards.
                                A yes/no prop where the book only ever prices one side (Anytime TD and
                                friends usually have no real "No" price) gets a single full-width button
                                instead of a second box that would just sit there empty. */}
                            {isYesNo && (!over || !under) ? (
                              <div className="flex-1">
                                <button
                                  onClick={() => onAddToBetSlip(game, market, over || under)}
                                  className="h-full w-full flex flex-col items-center justify-center py-3 text-center transition active:scale-95"
                                  style={{
                                    background: (over ? overInSlip : underInSlip) ? colors.cardBgActive : 'rgba(10,3,15,0.95)',
                                    border: (over ? overInSlip : underInSlip) ? `1px solid ${colors.gold}` : 'none',
                                  }}
                                >
                                  <div className="text-sm font-bold" style={{ color: colors.textPrimary }}>{over ? overLabel : underLabel}</div>
                                  <div className="text-xs font-bold mt-0.5" style={{ color: colors.odds }}>
                                    {formatOdds((over || under).price)}
                                  </div>
                                </button>
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 flex-1" style={{ gap: '1px', background: 'rgba(255,255,255,0.08)' }}>
                                {over ? (
                                  <button
                                    onClick={() => onAddToBetSlip(game, market, over)}
                                    className="h-full flex flex-col items-center justify-center py-3 text-center transition active:scale-95"
                                    style={{ background: overInSlip ? colors.cardBgActive : 'rgba(10,3,15,0.95)', border: overInSlip ? `1px solid ${colors.gold}` : 'none' }}
                                  >
                                    <div className="text-sm font-bold" style={{ color: colors.textPrimary }}>{overLabel}</div>
                                    <div className="text-xs font-bold mt-0.5" style={{ color: colors.odds }}>
                                      {formatOdds(over.price)}
                                    </div>
                                  </button>
                                ) : <div style={{ background: 'rgba(10,3,15,0.95)' }} />}
                                {under ? (
                                  <button
                                    onClick={() => onAddToBetSlip(game, market, under)}
                                    className="h-full flex flex-col items-center justify-center py-3 text-center transition active:scale-95"
                                    style={{ background: underInSlip ? colors.cardBgActive : 'rgba(10,3,15,0.95)', border: underInSlip ? `1px solid ${colors.gold}` : 'none' }}
                                  >
                                    <div className="text-sm font-bold" style={{ color: colors.textPrimary }}>{underLabel}</div>
                                    <div className="text-xs font-bold mt-0.5" style={{ color: colors.odds }}>
                                      {formatOdds(under.price)}
                                    </div>
                                  </button>
                                ) : <div style={{ background: 'rgba(10,3,15,0.95)' }} />}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Outright / yes-no props (goal scorers, anytime scorer, etc.) */}
                  {outrightList.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      {outrightList.map(({ outcome, market }, i) => {
                        const inSlip = isInBetSlip(market, outcome);
                        return (
                          <button
                            key={i}
                            onClick={() => onAddToBetSlip(game, market, outcome)}
                            className="py-4 px-4 rounded-xl text-center transition active:scale-95 flex-1"
                            style={{
                              minWidth: '140px',
                              maxWidth: outrightList.length === 1 ? '100%' : outrightList.length === 2 ? 'calc(50% - 4px)' : 'calc(33.33% - 6px)',
                              ...(inSlip
                                ? { background: colors.goldActiveBg, border: `2px solid ${colors.gold}` }
                                : { background: colors.cardBg, border: 'none' })
                            }}
                          >
                            <div className="text-sm font-semibold mb-2" style={{ color: colors.textPrimary }}>{outcome.name}</div>
                            <div className="text-base font-bold" style={{ color: inSlip ? colors.textPrimary : colors.odds }}>{formatOdds(outcome.price)}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const organizedProps = props ? organizeGamePropsByCategory(props) : { lines: [], halves: [], quarters: [], periods: [], innings: [], sets: [], rounds: [], outrights: [], teamTotals: [], oddEven: [], players: [], specials: [], maps: [] };

  const TABS = [
    { key: 'lines',      label: 'Game Lines',      count: organizedProps.lines.length },
    { key: 'maps',       label: 'Maps',            count: organizedProps.maps.length },
    { key: 'players',    label: 'Player Props',    count: organizedProps.players.length },
    { key: 'outrights',  label: 'Outrights',       count: organizedProps.outrights.length },
    { key: 'halves',     label: 'Halves',          count: organizedProps.halves.length },
    { key: 'periods',    label: 'Periods',         count: organizedProps.periods.length },
    { key: 'quarters',   label: 'Quarters',        count: organizedProps.quarters.length },
    { key: 'innings',    label: '1st 5 / Innings', count: organizedProps.innings.length },
    { key: 'sets',       label: 'Sets',            count: organizedProps.sets.length },
    { key: 'rounds',     label: 'Rounds',          count: organizedProps.rounds.length },
    { key: 'teamTotals', label: 'Team Totals',     count: organizedProps.teamTotals.length },
    { key: 'oddEven',    label: 'Odd/Even',        count: organizedProps.oddEven.length },
    { key: 'specials',   label: 'Specials',        count: organizedProps.specials.length },
  ].filter(t => t.count > 0);

  // Auto-select first available tab
  const activeCategory = selectedCategory && TABS.find(t => t.key === selectedCategory) ? selectedCategory : TABS[0]?.key;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="relative rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col" style={{ background: 'rgba(20,5,25,0.97)', boxShadow: colors.shadowRaised, color: colors.textPrimary, fontFamily: 'Nunito, sans-serif' }}>
        {/* Header */}
        <div className="p-4 flex justify-between items-center flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'linear-gradient(to right, rgba(99,41,101,0.3), rgba(50,20,30,0.4))' }}>
          <div>
            <h2 className="text-xl font-bold" style={{ color: colors.textPrimary }}>
              {game.away_team} @ {game.home_team}
            </h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-full transition" style={{ color: colors.textMuted }}>
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Category Tabs — only show when more than one tab has data */}
        {TABS.length > 1 && (
          <div
            ref={tabScroll.ref}
            className="flex-shrink-0 overflow-x-auto scrollbar-hide drag-scroll"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
            onMouseDown={tabScroll.onMouseDown}
            onMouseLeave={tabScroll.onMouseLeave}
            onMouseUp={tabScroll.onMouseUp}
            onMouseMove={tabScroll.onMouseMove}
          >
            <div className="flex min-w-max">
              {TABS.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setSelectedCategory(tab.key)}
                  className="px-4 py-3 font-medium border-b-2 transition whitespace-nowrap text-sm"
                  style={activeCategory === tab.key
                    ? { borderBottomColor: '#BF953F', color: colors.textPrimary, background: colors.goldActiveBg }
                    : { borderBottomColor: 'transparent', color: colors.textMuted }}
                >
                  {tab.label}
                  <span className="ml-1.5 text-xs px-1.5 py-0.5 rounded" style={{ background: colors.goldActiveBg, color: colors.textPrimary }}>{tab.count}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 scrollbar-hide">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent mx-auto mb-4" style={{ borderColor: '#BF953F', borderTopColor: 'transparent' }}></div>
              <p style={{ color: colors.textMuted }}>Loading {title.toLowerCase()}...</p>
            </div>
          ) : !props || props.bookmakers?.length === 0 ? (
            <div className="text-center py-12" style={{ color: colors.textMuted }}>
              <p className="text-lg mb-2">No {title.toLowerCase()} available</p>
              <p className="text-sm">These markets may not be offered for this game yet</p>
            </div>
          ) : (
            <div>
              {(() => {
                const markets = organizedProps[activeCategory] || [];
                if (markets.length === 0) {
                  return (
                    <div className="text-center py-12" style={{ color: colors.textMuted }}>
                      No {TABS.find(t => t.key === activeCategory)?.label || activeCategory} markets available
                    </div>
                  );
                }
                // For period-grouped categories, display grouped by period label
                const periodGroupConfig = {
                  quarters: { pattern: /_(q[1-4])$/, label: p => `Quarter ${p[1]}` },
                  halves:   { pattern: /_(h[12])$/,  label: p => p === 'h1' ? '1st Half' : '2nd Half' },
                  periods:  { pattern: /_(p[1-3])$/, label: p => `Period ${p[1]}` },
                  innings:  { pattern: /_(f5|i[1-9])$/, label: p => p === 'f5' ? '1st 5 Innings' : `Inning ${p[1]}` },
                  sets:     { pattern: /_(s[1-5])$/, label: p => `Set ${p[1]}` },
                };
                const groupCfg = periodGroupConfig[activeCategory];
                if (groupCfg) {
                  // Sort order within each period: ML → spread → total → team totals → others
                  const marketSortKey = (key) => {
                    if (/^h2h/.test(key)) return 0;
                    if (/^spreads/.test(key)) return 1;
                    if (/^totals/.test(key)) return 2;
                    if (/^team_total/.test(key)) return 3;
                    return 4;
                  };
                  const periodKeys = [...new Set(markets.map(m => {
                    const match = m.key.match(groupCfg.pattern);
                    return match ? match[1] : 'other';
                  }))].sort();
                  return (
                    <div className="space-y-6">
                      {periodKeys.map(pKey => {
                        const periodMarkets = markets
                          .filter(m => m.key.endsWith(`_${pKey}`))
                          .sort((a, b) => marketSortKey(a.key) - marketSortKey(b.key));
                        if (periodMarkets.length === 0) return null;
                        return (
                          <div key={pKey}>
                            <h3 className="text-sm font-bold mb-3 pb-1" style={{ color: colors.textPrimary, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                              {groupCfg.label(pKey)}
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                              {periodMarkets.map(m => renderMarket(m))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }
                // Player props — special layout with category headers and large buttons
                if (activeCategory === 'players') {
                  return renderPlayerPropsTab(markets);
                }
                // Default: simple grid
                return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{markets.map(m => renderMarket(m))}</div>;
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}>
          <div className="flex justify-start">
            <button
              onClick={onClose}
              className="px-8 py-2.5 rounded-lg transition font-semibold shadow-md"
              style={{ background: '#BF953F', color: '#200e22' }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}